import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, Platform } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';
import { useContentFeed } from '../hooks/useContentFeed.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { formatAppDate } from '../utils/locale.js';
import { DeviceTimezoneLabel } from '../components/DeviceTimezoneLabel.jsx';
import { BatchPreview } from '../components/preview/BatchPreview.jsx';
import { normalizePost } from '../components/preview/normalizePost.js';
import { RepublishDialog } from '../components/RepublishDialog.jsx';
import { CreditConfirmationModal } from '../components/CreditConfirmationModal.jsx';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { friendlyErrorMessage, notify } from '../lib/notifications.js';
import { createPollingClock } from '../utils/pollingClock.js';
import SocialConnectConfirm from '../components/billing/SocialConnectConfirm.jsx';
import { buildPostingDisconnectCopy, countConnectedSocials } from '../lib/postingPlan.js';
import { resolveSocialAddonState, wouldExceedIncluded, buildSocialAddonDisconnectCopy, isReauthOfConnectedAccount } from '../lib/socialAddon.js';
import { track } from '../lib/funnel.js';

// Languages we generate posts in. Kept in sync with the supported set on
// auth-worker (handlers/social-accounts.ts SUPPORTED_PUBLISH_LANGUAGES).
const PUBLISH_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pl', label: 'Polski' },
  { code: 'cs', label: 'Čeština' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'sv', label: 'Svenska' },
  { code: 'lt', label: 'Lietuvių' },
  { code: 'lv', label: 'Latviešu' },
];
const publishLanguageLabel = (code) => PUBLISH_LANGUAGES.find(l => l.code === code)?.label || (code ? code.toUpperCase() : 'Default');

const POST_PREVIEW_INTENT_KEY = 'post_preview_intent';

// POST EDITOR
export const PostEditor = ({ navigate, content, onContentUpdated, backTo = 'content', backOptions = null, backLabel = 'Back' }) => {
  const { t } = useTranslation();
  const { refreshContent } = useContentFeed();
  const [text, setText] = useState(contentText(content) || t('misc.editorEmptyPlaceholder'));
  const [platforms, setPlatforms] = useState(contentPlatforms(content));
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [targetAccounts, setTargetAccounts] = useState([]);
  const [platformRegistry, setPlatformRegistry] = useState({});
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [targetLoadError, setTargetLoadError] = useState(null);
  const [scheduledPost, setScheduledPost] = useState(null);

  useEffect(() => {
    setText(contentText(content) || t('misc.editorEmptyPlaceholder'));
    setPlatforms(contentPlatforms(content));
    setScheduledPost(null);
  }, [content]);

  const contentId = content?.contentItem?.id;
  const versionId = contentVersionId(content);
  const targetPlatforms = normalizeTargetPlatforms(platforms);
  const targetPlatformKey = targetPlatforms.join('|');
  const preferredTargetIds = Array.isArray(content?.publishTargetAccountIds) ? content.publishTargetAccountIds : [];
  const preferredTargetKey = preferredTargetIds.join('|');
  const targetRows = targetAccounts.map(account => {
    const platform = normalizeSocialPlatform(account.platform);
    const catalogItem = fallbackCatalogItem(platform);
    const displayName = displaySocialAccountName(account, catalogItem);
    const blockedReason = publishTargetBlockedReason(account, platformRegistry);
    return {
      id: account.id,
      platform,
      name: displayName,
      secondaryText: socialAccountSecondaryText(account, displayName) || connectedCaption({ ...account, types: catalogItem.types }),
      ready: !blockedReason,
      blockedReason,
    };
  });
  const selectedReadyTargets = targetRows.filter(row => row.ready && selectedTargetIds.includes(row.id));
  const selectedPreviewTarget = selectedReadyTargets[0] || null;
  const editorPlatformLabel = selectedReadyTargets.length
    ? selectedReadyTargets.map(target => target.name).join(', ')
    : targetPlatforms.map(platformLabel).join(', ') || t('misc.post');
  const previewPlatform = selectedPreviewTarget?.platform || targetPlatforms[0] || 'linkedin';
  const publishMode = ['now', 'schedule', 'draft'].includes(content?.publishMode) ? content.publishMode : 'now';
  const requestedScheduleAt = parseFutureDate(content?.publishScheduleAt);
  const editorLifecycle = contentLifecycle(content, false, t);
  const scheduleButtonLabel = ['published', 'inbox'].includes(editorLifecycle.status)
    ? t('misc.published')
    : ['scheduled', 'planned'].includes(editorLifecycle.status)
      ? t('misc.openScheduler')
      : publishMode === 'now'
        ? t('misc.publishSoon')
        : requestedScheduleAt ? t('misc.scheduleForTime', { time: requestedScheduleAt.toLocaleString() }) : t('misc.schedule');
  const instagramPlaceholderSelected = selectedReadyTargets.some(target => target.platform === 'instagram') && content?.contentItem?.format === 'text_post';

  useEffect(() => {
    let cancelled = false;
    async function loadPublishTargets() {
      setLoadingTargets(true);
      setTargetLoadError(null);
      try {
        const [accountResponse, registryResponse] = await Promise.all([
          api.socialAccounts.list(),
          api.platforms.registry(),
        ]);
        if (cancelled) return;
        const registry = Object.fromEntries((registryResponse.platforms || []).map(item => [normalizeSocialPlatform(item.platform), item]));
        const accounts = (accountResponse.accounts || [])
          .filter(account => targetPlatforms.includes(normalizeSocialPlatform(account.platform)));
        setPlatformRegistry(registry);
        setTargetAccounts(accounts);
        setSelectedTargetIds(previous => defaultSelectedTargetIds(accounts, registry, previous, preferredTargetIds));
      } catch (err) {
        if (!cancelled) {
          setTargetAccounts([]);
          setTargetLoadError(friendlyErrorMessage(err, t('misc.errorLoadPublishTargets')));
        }
      } finally {
        if (!cancelled) {
          setLoadingTargets(false);
        }
      }
    }
    loadPublishTargets();
    return () => {
      cancelled = true;
    };
  }, [targetPlatformKey, preferredTargetKey]);

  async function saveDraft() {
    if (!contentId) {
      setError(t('misc.errorSaveNoContent'));
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.content.createManualEdit(contentId, {
        caption: text,
        body: text,
        platformVersions: Object.fromEntries(platforms.map(platform => [platform, text])),
      });
      onContentUpdated?.({ contentItem: result.contentItem, activeVersion: result.activeVersion });
      setMessage(null);
      notify('post.savedDraft');
    } catch (err) {
      notify('post.saveFailed', { error: err });
    } finally {
      setSaving(false);
    }
  }

  async function regenerate() {
    if (!contentId) {
      setError(t('misc.errorRegenerateNoContent'));
      return;
    }
    setRegenerating(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.content.regenerate(contentId, {
        mode: 'full',
        instructions: 'Improve clarity and platform fit from the editor.',
        targetPlatforms: platforms,
        confirmCreditSpend: true,
      });
      const updated = { contentItem: result.contentItem, activeVersion: result.activeVersion };
      onContentUpdated?.(updated);
      setText(contentText(updated));
      setMessage(null);
      notify('post.regenerated');
    } catch (err) {
      notify('post.regenerateFailed', { error: err });
    } finally {
      setRegenerating(false);
    }
  }

  async function scheduleSoon() {
    if (!contentId || !versionId) {
      setError(t('misc.errorScheduleNoContent'));
      return;
    }
    if (scheduledPost) {
      setError(t('misc.errorAlreadyScheduled'));
      return;
    }
    if (editorLifecycle.status === 'published') {
      setError(t('misc.errorAlreadyPublished'));
      return;
    }
    if (['scheduled', 'planned'].includes(editorLifecycle.status)) {
      navigate('scheduler');
      return;
    }
    if (!selectedReadyTargets.length) {
      setError(t('misc.errorChoosePublishAccount'));
      return;
    }
    setScheduling(true);
    setError(null);
    setMessage(null);
    try {
      const scheduledAtDate = publishMode === 'now'
        ? new Date()
        : requestedScheduleAt || new Date(Date.now() + 15 * 60 * 1000);
      const scheduledAt = scheduledAtDate.toISOString();
      const targets = selectedReadyTargets.map(account => ({
        platform: account.platform,
        socialAccountId: account.id,
        priority: publishMode === 'now' ? 'now' : 'scheduled',
        scheduledAt,
      }));
      const result = await api.scheduler.create({
        contentItemId: contentId,
        contentVersionId: versionId,
        scheduledAt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        mode: publishMode === 'now' ? 'publish_now' : 'manual',
        targets,
      });
      setScheduledPost(result.post || result);
      onContentUpdated?.(contentWithSchedule(content, result));
      await refreshContent({ background: true });
      const accountText = t('misc.accountCount', { count: targets.length });
      const scheduleText = new Date(result.post?.scheduledAt || result.scheduledAt || scheduledAt).toLocaleString();
      setMessage(publishMode === 'now' ? t('misc.publishingStartedFor', { accounts: accountText }) : t('misc.scheduledForOn', { time: scheduleText, accounts: accountText }));
    } catch (err) {
      notify('post.scheduleFailed', { error: err });
    } finally {
      setScheduling(false);
    }
  }

  function toggleTargetAccount(id) {
    setSelectedTargetIds(value => value.includes(id) ? value.filter(item => item !== id) : [...value, id]);
  }

  return (
    <div className="page editor-page">
      <div className="editor-compact-header">
        <button className="btn btn-ghost" onClick={() => navigate(backTo, backOptions || {})}><Icon name="chev_l" size={14}/> {backLabel}</button>
        <img className="editor-mobile-header-logo" src="/logo.png" alt="Dathent" />
        <div>
          <h2 className="h2" style={{fontSize:17}}>{t('misc.editPost')}</h2>
          <div className="muted" style={{fontSize:12, marginTop:1}}>{editorPlatformLabel}</div>
        </div>
        <div className="row editor-actions-bar editor-actions-desktop" style={{gap:8}}>
              <button className="btn" onClick={regenerate} disabled={regenerating || !contentId}><Icon name="refresh" size={13}/> {regenerating ? t('misc.regenerating') : t('misc.regenerate')}</button>
              <button className="btn" onClick={saveDraft} disabled={saving || !contentId}>{saving ? t('misc.saving') : t('misc.saveDraft')}</button>
              <button className="btn btn-primary" onClick={scheduleSoon} disabled={scheduling || scheduledPost || editorLifecycle.status === 'published' || !contentId || !versionId}>{scheduling ? t('misc.scheduling') : scheduledPost ? t('misc.scheduled') : scheduleButtonLabel}</button>
            </div>
      </div>
      <div className="editor-layout">
        <div className="card">
          {error && <div className="auth-error" style={{ marginBottom: 12 }}><Icon name="alert" size={14} /> {error}</div>}
          {message && (
            <div className="auth-note editor-schedule-note">
              <div className="row" style={{ gap: 8 }}><Icon name="checkCircle" size={14} /> <span>{message}</span></div>
              {scheduledPost && (
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('scheduler')}>{t('misc.openSchedulerLower')}</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate('create')}>{t('misc.createAnother')}</button>
                </div>
              )}
            </div>
          )}
          <div className="form-section">
            <div className="label">{t('misc.postContent')}</div>
            <textarea className="field field-post" rows={14} value={text} onChange={e => setText(e.target.value)}/>
            <div className="row between" style={{marginTop:8}}>
              <span className="muted" style={{fontSize:11}}>{t('misc.charactersCount', { count: text.length })}</span>
              <button className="btn btn-sm" onClick={regenerate} disabled={regenerating || !contentId} style={{fontSize:12}}><Icon name="refresh" size={12}/> {regenerating ? t('misc.regenerating') : t('misc.regenerate')}</button>
            </div>
          </div>
          <div className="form-section">
            <div className="label">{t('misc.hashtags')}</div>
            <div className="row" style={{gap:6, flexWrap:'wrap'}}>
              {['#founder','#productivity','#consistency','#contentcreator','#solopreneur'].map(t => <span key={t} className="chip">{t}</span>)}
            </div>
          </div>
          <div className="form-section">
            <div className="row between" style={{ alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div className="label">{t('misc.whereToPublish')}</div>
                <div className="caption">{selectedReadyTargets.length ? t('misc.accountsSelected', { count: selectedReadyTargets.length }) : t('misc.chooseConnectedAccounts')}</div>
              </div>
            </div>
            {targetLoadError && <div className="auth-error" style={{ marginTop: 10 }}><Icon name="alert" size={14} /> {targetLoadError}</div>}
            {loadingTargets ? (
              <div className="publish-target-empty">{t('misc.loadingPublishTargets')}</div>
            ) : targetRows.length ? (
              <div className="publish-target-grid">
                {targetRows.map(account => {
                  const selected = selectedTargetIds.includes(account.id);
                  return (
                    <button key={account.id} className={'publish-target-card ' + (selected ? 'on ' : '') + (!account.ready ? 'is-disabled' : '')} disabled={!account.ready} onClick={() => toggleTargetAccount(account.id)}>
                      <Platform name={account.platform} size={22}/>
                      <div className="grow" style={{ textAlign: 'left', minWidth: 0 }}>
                        <div style={{ fontWeight: 650, fontSize: 13 }}>{account.name}</div>
                        <div className="caption">{account.blockedReason || account.secondaryText}</div>
                      </div>
                      {selected && <Icon name="check" size={14}/>} 
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="publish-target-empty">{t('misc.noConnectedAccountsMatch')}</div>
            )}
            {instagramPlaceholderSelected && (
              <div className="auth-note" style={{ marginTop: 10 }}>
                <Icon name="image" size={14} /> {t('misc.instagramTextPlaceholderNote')}
              </div>
            )}
          </div>
          <div className="form-section">
            <div className="label">{t('misc.aiActions')}</div>
            <div className="row" style={{gap:6, flexWrap:'wrap'}}>
              <button className="ai-action" onClick={regenerate} disabled={regenerating || !contentId}>{t('misc.rewrite')}</button>
              <button className="ai-action" disabled>{t('misc.shortenSoon')}</button>
              <button className="ai-action" disabled>{t('misc.moreProfessionalSoon')}</button>
              <button className="ai-action" disabled>{t('misc.moreViralSoon')}</button>
              <button className="ai-action" disabled>{t('misc.addCtaSoon')}</button>
              <button className="ai-action" disabled>{t('misc.translateSoon')}</button>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{fontSize:13}}>{t('misc.platformPreview', { platform: platformLabel(previewPlatform) })}</h3></div>
          <PlatformPreview platform={previewPlatform} target={selectedPreviewTarget} text={text} />
        </div>
      </div>
      {/* Mobile-only fixed bottom action bar */}
      <div className="editor-mobile-bar">
        <button className="btn" onClick={saveDraft} disabled={saving || !contentId} style={{flex:'0 0 40%'}}>{saving ? t('misc.saving') : t('misc.saveDraft')}</button>
        <button className="btn btn-primary" onClick={scheduleSoon} disabled={scheduling || scheduledPost || editorLifecycle.status === 'published' || !contentId || !versionId} style={{flex:'0 0 calc(60% - 8px)'}}>{scheduling ? t('misc.scheduling') : scheduledPost ? t('misc.scheduled') : scheduleButtonLabel}</button>
      </div>
    </div>
  );
};

export const PostPreview = ({ navigate, content, onContentUpdated, backTo = 'content', backOptions = null, backLabel = 'Back' }) => {
  const { t } = useTranslation();
  const { openUpgrade } = useUpgrade();
  const dash = useDashboardData() || {};
  const { content: feedContent, refreshContent, hydrateContentItem, upsertContentItems } = useContentFeed();
  const [menuOpen, setMenuOpen] = useState(false);
  const [working, setWorking] = useState(null);
  const [error, setError] = useState(null);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [platformRegistry, setPlatformRegistry] = useState({});
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [republishOpen, setRepublishOpen] = useState(false);
  const [rescheduleAt, setRescheduleAt] = useState(() => toDatetimeLocalValue(bestTimeDate()));
  const [activationSkipped, setActivationSkipped] = useState(() => typeof window !== 'undefined' && window.sessionStorage.getItem('activation_social_connect_skipped') === '1');
  const [activationStatus, setActivationStatus] = useState(null);
  const [telegramLink, setTelegramLink] = useState(null);
  const [imageRetrying, setImageRetrying] = useState(false);
  const [creditUsage, setCreditUsage] = useState(null);
  const [creditAction, setCreditAction] = useState(null);
  const [creditConfirming, setCreditConfirming] = useState(false);
  const [publishPanel, setPublishPanel] = useState(null);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  const [publishNotice, setPublishNotice] = useState(null);
  const [socialConnectPlatform, setSocialConnectPlatform] = useState(null);
  const republishRefreshTimersRef = useRef([]);
  const seedContentId = content?.contentItem?.id;
  const feedMatch = seedContentId ? feedContent.find(item => item.id === seedContentId)?.raw : null;
  const currentContent = mergePreviewContent(feedMatch, content);
  const contentId = currentContent?.contentItem?.id;
  const text = contentText(currentContent) || t('misc.generationInProgress');
  const platforms = contentPlatforms(currentContent);
  const previewPlatform = platforms[0] || 'instagram';
  const firstPostMeta = firstPostActivationMeta(currentContent);
  const isFirstPost = Boolean(firstPostMeta);
  const firstPostDetails = firstPostActivationDetails(currentContent, previewPlatform, firstPostMeta);
  const title = isFirstPost ? t('misc.firstPostReady') : postPreviewTitle(currentContent);
  const generating = currentContent?.contentItem?.status === 'generating' || currentContent?.contentItem?.generationStatus && !['ready', 'failed'].includes(currentContent.contentItem.generationStatus);
  const lifecycle = contentLifecycle(currentContent, generating, t);
  const primaryAction = previewPrimaryAction(lifecycle, isFirstPost);
  const previewPost = normalizePost(currentContent);
  const hasPreviewImage = hasContentImage(currentContent, previewPost);
  const previewTargets = previewTargetsFromContent(currentContent, socialAccounts, platforms);
  const versionId = contentVersionId(currentContent);
  const publishTargetRows = publishTargetRowsForPlatforms(socialAccounts, platformRegistry, platforms);
  const selectedPublishTargetRows = publishTargetRows.filter(row => selectedTargetIds.includes(row.id) && !row.blockedReason);
  const canPublishNow = Boolean(contentId && versionId && !['published', 'inbox', 'publishing', 'generating'].includes(lifecycle.status));
  const canCreatePublish = Boolean(contentId && versionId && !['published', 'inbox', 'publishing', 'generating', 'scheduled', 'planned'].includes(lifecycle.status));
  const canUsePublishPanel = publishPanel === 'publish' ? canPublishNow : canCreatePublish;
  const publishPanelDisabledReason = inlinePublishDisabledReason({ contentId, versionId, lifecycle, selectedPublishTargetRows, mode: publishPanel });
  const activationRows = isFirstPost ? activationConnectRows(platforms, socialAccounts, platformRegistry) : [];
  const hasReadyActivationTarget = activationRows.some(row => row.connected && row.publishReady);
  const statusIcon = lifecycleIcon(lifecycle.status);
  const statusMessage = isFirstPost && lifecycle.status === 'ready'
    ? t('misc.firstPostStatusMessage')
    : lifecycle.message || lifecycleFallbackMessage(lifecycle.status);

  useEffect(() => {
    const intent = readPostPreviewIntent(contentId);
    setScheduleOpen(intent === 'schedule' && lifecycle.status === 'scheduled');
    setPublishPanel(intent === 'publish' || (intent === 'schedule' && lifecycle.status !== 'scheduled') ? intent : null);
    setPublishNotice(null);
  }, [contentId]);

  useEffect(() => {
    setRescheduleAt(toDatetimeLocalValue(lifecycle.scheduledAt || bestTimeDate()));
  }, [contentId, lifecycle.scheduledAt]);

  useEffect(() => {
    const preferredTargetIds = Array.isArray(currentContent?.publishTargetAccountIds) ? currentContent.publishTargetAccountIds : [];
    setSelectedTargetIds(previous => defaultSelectedTargetIds(publishTargetRows.map(row => row.account), platformRegistry, previous, preferredTargetIds));
  }, [contentId, platforms.join('|'), socialAccounts, platformRegistry]);

  useEffect(() => {
    let cancelled = false;
    api.socialAccounts.list()
      .then(response => {
        if (!cancelled) setSocialAccounts(response.accounts || []);
      })
      .catch(() => {
        if (!cancelled) setSocialAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      republishRefreshTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
      republishRefreshTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.platforms.registry()
      .then(response => {
        if (!cancelled) setPlatformRegistry(Object.fromEntries((response.platforms || []).map(item => [normalizeSocialPlatform(item.platform), item])));
      })
      .catch(() => {
        if (!cancelled) setPlatformRegistry({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.billing.getEntitlements()
      .then(response => {
        if (!cancelled) setCreditUsage(response.usage?.publish_ready_posts_monthly || response.usage?.ai_generations_monthly || null);
      })
      .catch(() => {
        if (!cancelled) setCreditUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!contentId || !['generating', 'publishing', 'scheduled'].includes(lifecycle.status)) return undefined;
    return createPollingClock(() => refreshContent({ background: true }), {
      minIntervalMs: 4000,
      maxIntervalMs: 60_000,
      fireImmediately: false,
    });
  }, [contentId, lifecycle.status]);

  if (!currentContent) {
    return (
      <div className="page post-preview-page post-preview-empty">
        <div className="card content-empty-v2">
          <Icon name="grid" size={24} />
          <h2 className="h2" style={{ margin: '12px 0 8px' }}>{t('misc.noPostSelected')}</h2>
          <p className="body" style={{ color: 'var(--text-3)', marginBottom: 18 }}>{t('misc.noPostSelectedHint')}</p>
          <button className="btn btn-primary" onClick={() => navigate('content')}>{t('misc.openLibrary')}</button>
        </div>
      </div>
    );
  }

  async function regeneratePost() {
    if (!contentId) return;
    setWorking('regenerate');
    setError(null);
    try {
      const result = await api.content.regenerate(contentId, { mode: 'full', targetPlatforms: platforms, confirmCreditSpend: true });
      const updated = { contentItem: result.contentItem, activeVersion: result.activeVersion, firstPost: firstPostMeta || null };
      upsertContentItems([updated]);
      onContentUpdated?.(updated);
      setMenuOpen(false);
      notify('post.regenerated');
    } catch (err) {
      notify('post.regenerateFailed', { error: err });
    } finally {
      setWorking(null);
    }
  }

  async function reschedulePost() {
    if (!lifecycle.scheduledPostId) return;
    const scheduledAt = datetimeLocalToIso(rescheduleAt);
    if (!scheduledAt) {
      setError(t('misc.pickValidFutureTime'));
      return;
    }
    setWorking('reschedule');
    setError(null);
    try {
      await api.scheduler.reschedule(lifecycle.scheduledPostId, {
        scheduledAt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      await refreshContent({ background: true });
      setScheduleOpen(false);
      notify('post.rescheduled');
    } catch (err) {
      notify('post.rescheduleFailed', { error: err });
    } finally {
      setWorking(null);
    }
  }

  function openInlinePublishPanel(mode) {
    if (mode === 'schedule' && lifecycle.status === 'scheduled') {
      setScheduleOpen(open => !open);
      setPublishPanel(null);
      return;
    }
    setMenuOpen(false);
    setScheduleOpen(false);
    setPublishNotice(null);
    setPublishPanel(current => current === mode ? null : mode);
  }

  function togglePublishTarget(id) {
    setSelectedTargetIds(values => values.includes(id) ? values.filter(value => value !== id) : [...values, id]);
  }

  async function createInlinePublish(mode) {
    const publishNow = mode === 'publish';
    if (!(publishNow ? canPublishNow : canCreatePublish) || !contentId || !versionId) return;
    const scheduledAt = publishNow ? new Date().toISOString() : datetimeLocalToIso(rescheduleAt);
    if (!scheduledAt) {
      setPublishNotice({ type: 'error', message: t('misc.pickValidFutureTime') });
      return;
    }
    if (!selectedPublishTargetRows.length) {
      setPublishNotice({ type: 'error', message: t('misc.chooseAtLeastOneAccount') });
      return;
    }
    setWorking(publishNow ? 'publish-now' : 'schedule-create');
    setError(null);
    setPublishNotice(null);
    try {
      const targets = selectedPublishTargetRows.map(row => ({
        platform: row.platform,
        socialAccountId: row.id,
        priority: publishNow ? 'now' : 'scheduled',
        scheduledAt,
      }));
      const result = await api.scheduler.create({
        contentItemId: contentId,
        contentVersionId: versionId,
        scheduledAt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        mode: publishNow ? 'publish_now' : 'manual',
        targets,
      });
      const updated = contentWithSchedule(currentContent, result);
      upsertContentItems([updated]);
      onContentUpdated?.(updated);
      await refreshContent({ background: true, refreshMetrics: false });
      setPublishNotice({
        type: 'success',
        message: publishNow
          ? lifecycle.status === 'scheduled'
            ? t('misc.publishingStartedForAccountsReplaced', { accounts: t('misc.accountCount', { count: targets.length }) })
            : t('misc.publishingStartedForAccountsSimple', { accounts: t('misc.accountCount', { count: targets.length }) })
          : t('misc.scheduledForTimeShort', { time: formatLifecycleDate(scheduledAt) }),
      });
    } catch (err) {
      setPublishNotice({ type: 'error', message: friendlyErrorMessage(err, publishNow ? t('misc.unableToPublishNow') : t('misc.unableToSchedulePost')) });
    } finally {
      setWorking(null);
    }
  }

  async function retryFailedPost() {
    if (!lifecycle.scheduledPostId) return;
    setWorking('retry');
    setError(null);
    try {
      await api.scheduler.retry(lifecycle.scheduledPostId);
      const updated = markPublishRetryPending(currentContent);
      onContentUpdated?.(updated);
      await refreshContent({ background: true });
      notify('post.retryStarted');
    } catch (err) {
      notify('post.retryFailed', { error: err });
    } finally {
      setWorking(null);
    }
  }

  async function retryFirstPostImage() {
    if (!isFirstPost || imageRetrying) return;
    setImageRetrying(true);
    setError(null);
    try {
      const result = await api.content.generateFirstPost({ includePremiumImage: true, retryImage: true });
      if (result.content) {
        const updated = {
          ...result.content,
          firstPost: {
            ...(firstPostMeta || {}),
            ...(result.firstPost || {}),
          }
        };
        onContentUpdated?.(updated);
        upsertContentItems([updated]);
        notify('post.imageRetryStarted');
      }
    } catch (err) {
      notify('post.imageRetryFailed', { error: err });
    } finally {
      setImageRetrying(false);
    }
  }

  function requestPaidAction(action) {
    setError(null);
    setMenuOpen(false);
    setCreditAction(action);
  }

  async function confirmPaidAction() {
    if (!creditAction) return;
    setCreditConfirming(true);
    try {
      if (creditAction.type === 'regenerate-text') {
        await regeneratePost();
      } else if (creditAction.type === 'full-week') {
        await api.strategy.generateWeeklyPlan({
          weekStartDate: new Date().toISOString().slice(0, 10),
          confirmCreditSpend: true,
        });
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem('onboarding_first_post_prefill', JSON.stringify({
            source: 'first_post_full_week',
            topic: firstPostDetails.topic || currentContent?.contentItem?.topic || 'Build a week from the first post direction',
            goal: 'awareness',
            platforms,
            count: 7,
          }));
        }
        navigate('create');
      } else if (creditAction.type === 'full-month') {
        await api.strategy.generateMonthlyPlan({
          monthStartDate: new Date().toISOString().slice(0, 10),
          confirmCreditSpend: true,
        });
        navigate('brand');
      }
      setCreditAction(null);
    } catch (err) {
      notify('post.paidActionFailed', { error: err });
    } finally {
      setCreditConfirming(false);
    }
  }

  function openInstagramFallback() {
    navigate('social');
  }

  async function startActivationConnect(platform, meta = {}) {
    const normalized = normalizeSocialPlatform(platform);
    setActivationStatus(null);
    setTelegramLink(null);
    if (!activationProviderImplemented(normalized, platformRegistry[normalized])) {
      setActivationStatus(t('misc.connectionComingSoon', { platform: platformLabel(normalized) }));
      return;
    }
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('activation_return_to_first_post', JSON.stringify({ platform: normalized, at: Date.now() }));
      }
      if (normalized === 'telegram') {
        const response = await api.socialAccounts.createTelegramLink();
        setTelegramLink(response);
        setActivationStatus(t('misc.telegramSetupReady'));
        return;
      }
      setActivationStatus(t('misc.redirectingTo', { platform: platformLabel(normalized) }));
      // overageAck only true when this came from <SocialConnectConfirm>'s
      // onContinue AND it was actually showing the priced overage sheet.
      await api.socialAccounts.connect(normalized, { overageAck: meta?.overageAck === true });
    } catch (err) {
      setActivationStatus(friendlyErrorMessage(err, t('misc.unableToConnect', { platform: platformLabel(normalized) })));
    }
  }

  function connectActivationPlatform(platform) {
    // TASK-329: generalises the Scheduler-only gate — a billable org over
    // its plan's included social-account count also gets the confirm
    // dialog now (billing adapts automatically once they continue).
    const social = dash.entitlements?.socialAccounts;
    if (dash.entitlements?.plan === 'posting' || wouldExceedIncluded(social)) {
      setSocialConnectPlatform(normalizeSocialPlatform(platform));
      return;
    }
    startActivationConnect(platform);
  }

  function skipActivationConnect() {
    if (typeof window !== 'undefined') window.sessionStorage.setItem('activation_social_connect_skipped', '1');
    setActivationSkipped(true);
  }

  function runPrimaryAction() {
    if (['published', 'generating', 'publishing'].includes(lifecycle.status)) return;
    if (lifecycle.status === 'failed' && lifecycle.failureKind !== 'generation') {
      retryFailedPost();
      return;
    }
    if (lifecycle.status === 'scheduled') {
      setScheduleOpen(open => !open);
      return;
    }
    if (lifecycle.status === 'planned') {
      navigate('scheduler');
      return;
    }
    if (isFirstPost && !hasReadyActivationTarget) {
      setActivationStatus(t('misc.connectBeforeSchedulingFirstPost'));
      return;
    }
    if (isFirstPost && hasReadyActivationTarget) {
      setRepublishOpen(true);
      return;
    }
    navigate('editor');
  }

  async function handleRepublishComplete(payload = {}) {
    setRepublishOpen(false);
    const republishedContent = payload.content?.contentItem ? contentWithSchedule(payload.content, payload.schedule) : null;
    const republishedContentId = payload.contentId || republishedContent?.contentItem?.id || null;
    if (republishedContent) {
      upsertContentItems([republishedContent]);
      if (republishedContent.contentItem.id === contentId) onContentUpdated?.(republishedContent);
    }
    scheduleRepublishConfirmationRefresh(republishedContentId);
    await refreshContent({ background: true, refreshMetrics: false });
  }

  function scheduleRepublishConfirmationRefresh(republishedContentId) {
    republishRefreshTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
    republishRefreshTimersRef.current = [1500, 6000].map(delay => window.setTimeout(async () => {
      try {
        if (republishedContentId) await hydrateContentItem(republishedContentId);
        await refreshContent({ background: true, refreshMetrics: false });
      } catch (_) {
        // The optimistic row is already visible; the next page visit will hydrate if this confirmation misses.
      }
    }, delay));
  }

  if (isFirstPost) {
    const primaryPlatformLabel = platformLabel(previewPlatform);
    const heroSubtitle = hasReadyActivationTarget
      ? t('misc.heroSubtitleReady')
      : activationSkipped
        ? t('misc.heroSubtitleSavedDraft', { platform: primaryPlatformLabel })
        : t('misc.heroSubtitleConnect', { platform: primaryPlatformLabel });
    const scheduleDisabled = !contentId || lifecycle.status === 'published' || lifecycle.status === 'generating';
    const editDisabled = !contentId || lifecycle.status === 'published';
    const imageDisabled = imageRetrying || !contentId || lifecycle.status === 'published';
    const fullWeekDisabled = !contentId;
    const detailRows = previewTargetRows(previewTargets, platforms, lifecycle);
    return (
      <div className="page post-preview-page first-post-activation-v3">
        <section className={'fpa-hero status-' + lifecycle.status}>
          <div className="fpa-hero-pills" role="list" aria-label={t('misc.firstPostStatus')}>
            <span className={'fpa-pill fpa-pill-status status-' + lifecycle.status} role="listitem">
              <Icon name={statusIcon} size={12} /> {lifecycle.label}
            </span>
            <PlatformBadge platform={previewPlatform} status={lifecycle.status} className="fpa-platform-badge" />
            <span className={'fpa-pill fpa-pill-connection ' + (hasReadyActivationTarget ? 'is-connected' : 'is-disconnected')} role="listitem">
              <Icon name={hasReadyActivationTarget ? 'check' : 'info'} size={12} />
              {hasReadyActivationTarget ? t('misc.connected') : t('misc.notConnected')}
            </span>
          </div>
          <h2 className="h2 fpa-hero-title">{t('misc.firstPostReady')}</h2>
          <p className="fpa-hero-subtitle">{heroSubtitle}</p>
        </section>

        {error && (
          <div className="auth-error fpa-banner" role="alert"><Icon name="alert" size={14} /> {error}</div>
        )}
        {generating && <GenerationProgressCard content={currentContent} />}
        {!generating && lifecycle.status === 'failed' && (
          <div className="auth-error fpa-banner" role="alert">
            <Icon name="alert" size={14} /> {lifecycleFailureMessage(lifecycle, previewPlatform)}
            {isInstagramBusinessPublishSetupError(lifecycle) && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 10 }} onClick={openInstagramFallback}>{t('misc.connectViaFacebookPage')}</button>
            )}
          </div>
        )}
        {!generating && lifecycle.status === 'inbox' && (
          <div className="auth-note fpa-banner post-inbox-banner">
            <Icon name="checkCircle" size={14} />
            <span><strong>{t('postPreview.inboxDraftTitle')}</strong>{lifecycle.message}</span>
          </div>
        )}
        {scheduleOpen && lifecycle.status === 'scheduled' && (
          <div className="card preview-schedule-card fpa-banner">
            <div>
              <div className="label">{t('misc.changePublishTime')}</div>
              <div className="caption" style={{ marginTop: 3 }}>{t('misc.changePublishTimeHint')} · <DeviceTimezoneLabel compact /></div>
            </div>
            <input className="input" type="datetime-local" value={rescheduleAt} min={toDatetimeLocalValue(new Date(Date.now() + 60 * 1000))} onChange={event => setRescheduleAt(event.target.value)} />
            <button className="btn btn-primary" disabled={working === 'reschedule'} onClick={reschedulePost}>{working === 'reschedule' ? t('misc.saving') : t('misc.saveTime')}</button>
          </div>
        )}

        <div className="fpa-layout">
          <div className="fpa-post-panel">
            <BatchPreview
              posts={previewPost ? [previewPost] : []}
              platforms={platforms}
              targets={previewTargets}
              topic={currentContent?.contentItem?.topic || title}
              batch={currentContent?.generationBatch || null}
              showActions={false}
              showHeader={false}
            />
            {!hasPreviewImage && lifecycle.status !== 'generating' && (
              <div className="fpa-image-placeholder">
                <div className="fpa-image-placeholder-icon"><Icon name="image" size={20} /></div>
                <div className="fpa-image-placeholder-text">
                  <strong>{t('misc.imageNotReady')}</strong>
                  <span>{t('misc.generatePremiumImageHint')}</span>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={retryFirstPostImage} disabled={imageDisabled}>
                  {imageRetrying ? t('misc.generatingImage') : t('misc.generateImage')}
                </button>
              </div>
            )}
          </div>

          <aside className="fpa-side" aria-label={t('misc.firstPostActions')}>
            <div className="card fpa-actions-card">
              <div className="eyebrow">{t('misc.nextStep')}</div>
              <h3 className="h3 fpa-actions-title">
                {hasReadyActivationTarget
                  ? t('misc.scheduleToPlatform', { platform: primaryPlatformLabel })
                  : activationSkipped
                    ? t('misc.savedAsDraft')
                    : t('misc.connectPlatformToSchedule', { platform: primaryPlatformLabel })}
              </h3>
              <div className="fpa-actions-row">
                {hasReadyActivationTarget ? (
                  <button
                    className="btn btn-primary fpa-actions-primary"
                    onClick={() => setRepublishOpen(true)}
                    disabled={scheduleDisabled}
                  >
                    <Icon name="calendar" size={14} /> {t('misc.scheduleThisPost')}
                  </button>
                ) : activationSkipped ? (
                  <>
                    <div className="fpa-draft-note">
                      <Icon name="check" size={13} /> {t('misc.postSavedConnectAnytime')}
                    </div>
                    <button
                      className="btn btn-secondary fpa-actions-primary"
                      onClick={() => connectActivationPlatform(previewPlatform)}
                      disabled={!contentId || lifecycle.status === 'published'}
                    >
                      <Icon name="link" size={14} /> {t('misc.connectPlatform', { platform: primaryPlatformLabel })}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-primary fpa-actions-primary"
                      onClick={() => connectActivationPlatform(previewPlatform)}
                      disabled={!contentId || lifecycle.status === 'published'}
                    >
                      <Icon name="link" size={14} /> {t('misc.connectPlatform', { platform: primaryPlatformLabel })}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={skipActivationConnect}
                    >
                      {t('misc.keepAsDraft')}
                    </button>
                  </>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => navigate('editor')} disabled={editDisabled}>
                  <Icon name="edit" size={14} /> {t('misc.editText')}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={retryFirstPostImage} disabled={imageDisabled}>
                  <Icon name="image" size={14} /> {imageRetrying ? t('misc.generatingImage') : hasPreviewImage ? t('misc.regenerateImage') : t('misc.generateImage')}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => requestPaidAction({ type: 'full-week', title: t('misc.generateFullWeek'), cost: 10 })}
                  disabled={fullWeekDisabled}
                >
                  <Icon name="calendar" size={14} /> {t('misc.generateFullWeek')}
                </button>
              </div>
              {activationStatus && (
                <div className="auth-note fpa-actions-note"><Icon name="info" size={13} /> {activationStatus}</div>
              )}
              {telegramLink?.linkUrl && (
                <a className="btn btn-secondary btn-sm fpa-actions-telegram" href={telegramLink.linkUrl} target="_blank" rel="noreferrer">
                  {t('misc.openTelegram')}
                </a>
              )}
            </div>

            <div className="card fpa-why-card">
              <div className="eyebrow">{t('misc.whyThisPost')}</div>
              <p className="fpa-why-text">{summarizeWhyThis(firstPostDetails.whyThisFirst)}</p>
              {firstPostDetails.imageWarning && (
                <div className="auth-note fpa-why-warning">
                  <Icon name="alert" size={13} /> {t('misc.premiumImageFailed', { error: firstPostDetails.imageWarning })}
                </div>
              )}
            </div>

            <details className="card fpa-details">
              <summary>{t('misc.viewGenerationDetails')}</summary>
              <div className="fpa-details-body">
                <div className="first-post-activation-grid">
                  <ActivationField label={t('misc.fieldPlatform')} value={platformLabel(firstPostDetails.platform)} />
                  <ActivationField label={t('misc.fieldFormat')} value={formatLabel(firstPostDetails.format)} />
                  <ActivationField label={t('misc.fieldHook')} value={firstPostDetails.hook} wide />
                  <ActivationField label={t('misc.fieldCta')} value={firstPostDetails.cta || '—'} />
                  <ActivationField label={t('misc.fieldHashtags')} value={firstPostDetails.hashtags.length ? firstPostDetails.hashtags.join(' ') : '—'} wide />
                  <ActivationField label={t('misc.fieldStrategyContext')} value={firstPostDetails.strategyContext} wide />
                </div>
                {(firstPostMeta?.imagePrompt || firstPostMeta?.visualPrompt) && (
                  <div className="fpa-details-section">
                    <div className="eyebrow">{t('misc.imagePrompt')}</div>
                    <div className="caption fpa-details-prompt">
                      {String(firstPostMeta.imagePrompt || firstPostMeta.visualPrompt)
                        .replace(/\b[a-z]+(?:_[a-z0-9]+){2,}\b/g, token => token.replace(/_/g, ' '))
                        .replace(/[ \t]+/g, ' ')}
                    </div>
                  </div>
                )}
                <div className="fpa-details-section">
                  <div className="eyebrow">{t('misc.publishingTargets')}</div>
                  <div className="post-preview-target-list-v2">
                    {detailRows.map(target => (
                      <div key={`${target.platform}-${target.id || target.socialAccountId || target.name}`} className="post-preview-target-row-v2">
                        <Platform name={target.platform} size={22} />
                        <div>
                          <div className="post-preview-target-name-v2">{targetDisplayName(target, target.platform)}</div>
                          <div className="caption">{targetCaptionLine(target, target.platform, t)}</div>
                        </div>
                        <span className={'post-target-status-dot status-' + (target.status || lifecycle.status)} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </details>
          </aside>
        </div>

        <div className="post-preview-mobile-bar editor-mobile-bar">
          <button className="btn" onClick={() => navigate('editor')} disabled={editDisabled} style={{ flex: '0 0 40%' }}>{t('misc.edit')}</button>
          <button
            className="btn btn-primary"
            onClick={hasReadyActivationTarget ? () => setRepublishOpen(true) : () => connectActivationPlatform(previewPlatform)}
            disabled={!contentId || lifecycle.status === 'published'}
            style={{ flex: '0 0 calc(60% - 8px)' }}
          >
            {hasReadyActivationTarget ? t('misc.schedule') : t('misc.connect')}
          </button>
        </div>

        <RepublishDialog
          open={republishOpen}
          content={currentContent}
          onClose={() => setRepublishOpen(false)}
          onComplete={handleRepublishComplete}
        />
        <CreditConfirmationModal
          open={Boolean(creditAction)}
          title={creditAction?.title || t('misc.confirmPlanQuota')}
          description={creditActionDescription(creditAction)}
          creditCost={creditAction?.cost || 1}
          creditUsage={creditUsage}
          confirming={creditConfirming || working === 'regenerate'}
          onConfirm={confirmPaidAction}
          onCancel={() => setCreditAction(null)}
          onBuyCredits={() => openUpgrade({ feature: 'quota' })}
          onUpgrade={() => openUpgrade({ feature: 'quota' })}
        />
      </div>
    );
  }

  return (
    <div className="page post-preview-page post-preview-v2">
      <section className={'post-preview-review-head status-' + lifecycle.status}>
        <div className="post-preview-review-copy">
          <div className="post-preview-status-row">
            <span className={'post-status-badge status-' + lifecycle.status}><Icon name={statusIcon} size={13} /> {lifecycle.label}</span>
            <span className="post-preview-platforms-inline" role="list" aria-label={t('misc.targetPlatforms')}>
              {platforms.map(platform => <PlatformBadge key={platform} platform={platform} status={lifecycle.status} />)}
            </span>
          </div>
          <h2 className="h2 post-preview-title-v2">{title}</h2>
          <p className="post-preview-subtitle-v2">{statusMessage}</p>
        </div>
        <div className="post-preview-actions-v2">
          {backLabel && backLabel !== 'Back' && (
            <button className="btn btn-ghost" onClick={() => navigate(backTo, backOptions || {})}>
              <Icon name="chev_l" size={14} /> {backLabel}
            </button>
          )}
          <button className="btn btn-primary" disabled={!canPublishNow || working === 'publish-now'} onClick={() => openInlinePublishPanel('publish')}><Icon name="send" size={14} /> {working === 'publish-now' ? t('misc.publishing') : t('misc.publishNow')}</button>
          <button className="btn btn-secondary" disabled={(!canCreatePublish && lifecycle.status !== 'scheduled') || working === 'schedule-create'} onClick={() => openInlinePublishPanel('schedule')}><Icon name="calendar" size={14} /> {lifecycle.status === 'scheduled' ? t('misc.changeTime') : t('misc.schedule')}</button>
          <button className="btn btn-secondary" onClick={() => navigate('editor')} disabled={!contentId || lifecycle.status === 'published'}><Icon name="edit" size={14} /> {t('misc.edit')}</button>
          <button className="btn btn-icon btn-ghost" aria-label={t('misc.morePostActions')} onClick={() => setMenuOpen(open => !open)}><Icon name="more" size={14} /></button>
          {menuOpen && (
            <div className="preview-action-menu card">
              <button onClick={() => navigate('editor')} disabled={!contentId || lifecycle.status === 'published'}>{t('misc.edit')}</button>
              <button onClick={() => openInlinePublishPanel('publish')} disabled={!canPublishNow}>{t('misc.publishNow')}</button>
              <button onClick={() => openInlinePublishPanel('schedule')} disabled={!canCreatePublish && lifecycle.status !== 'scheduled'}>{lifecycle.status === 'scheduled' ? t('misc.changeTime') : t('misc.schedule')}</button>
              <button onClick={() => requestPaidAction({ type: 'regenerate-text', title: t('misc.regenerateText'), cost: 1 })} disabled={!contentId || working === 'regenerate' || lifecycle.status === 'published'}>{working === 'regenerate' ? t('misc.regenerating') : t('misc.regenerateText')}</button>
              {lifecycle.status === 'failed' && lifecycle.scheduledPostId && <button onClick={retryFailedPost} disabled={working === 'retry'}>{working === 'retry' ? t('misc.retrying') : t('misc.retryPublish')}</button>}
              {lifecycle.scheduledPostId && <button onClick={() => navigate('scheduler', { focusPostId: lifecycle.scheduledPostId })} disabled={!contentId}>{t('misc.openScheduler')}</button>}
            </div>
          )}
        </div>
      </section>
      {error && <div className="auth-error" style={{ marginBottom: 12 }}><Icon name="alert" size={14} /> {error}</div>}
      {generating && <GenerationProgressCard content={currentContent} />}
      {!generating && lifecycle.status === 'failed' && (
        <div className="auth-error" style={{ marginBottom: 12 }}>
          <Icon name="alert" size={14} /> {lifecycleFailureMessage(lifecycle, previewPlatform)}
          {isInstagramBusinessPublishSetupError(lifecycle) && (
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 10 }} onClick={openInstagramFallback}>Connect via Facebook Page</button>
          )}
        </div>
      )}
      {!generating && lifecycle.status === 'inbox' && (
        <div className="auth-note post-inbox-banner" style={{ marginBottom: 12 }}>
          <Icon name="checkCircle" size={14} />
          <span><strong>{t('postPreview.inboxDraftTitle')}</strong>{lifecycle.message}</span>
        </div>
      )}
      {publishNotice && (!publishPanel || !canUsePublishPanel) && (
        <div className={(publishNotice.type === 'success' ? 'auth-note' : 'auth-error') + ' inline-publish-notice'} style={{ marginBottom: 12 }}>
          <Icon name={publishNotice.type === 'success' ? 'checkCircle' : 'alert'} size={14} /> {publishNotice.message}
        </div>
      )}
      {scheduleOpen && lifecycle.status === 'scheduled' && (
        <div className="card preview-schedule-card">
          <div>
            <div className="label">{t('misc.changePublishTime')}</div>
            <div className="caption" style={{ marginTop: 3 }}>{t('misc.changePublishTimeHint')} · <DeviceTimezoneLabel compact /></div>
          </div>
          <input className="input" type="datetime-local" value={rescheduleAt} min={toDatetimeLocalValue(new Date(Date.now() + 60 * 1000))} onChange={event => setRescheduleAt(event.target.value)} />
          <button className="btn btn-primary" disabled={working === 'reschedule'} onClick={reschedulePost}>{working === 'reschedule' ? t('misc.saving') : t('misc.saveTime')}</button>
        </div>
      )}
      {publishPanel && canUsePublishPanel && (
        <InlinePublishPanel
          mode={publishPanel}
          scheduleAt={rescheduleAt}
          onScheduleAtChange={setRescheduleAt}
          targetRows={publishTargetRows}
          selectedTargetIds={selectedTargetIds}
          onToggleTarget={togglePublishTarget}
          onSubmit={() => createInlinePublish(publishPanel)}
          onConnect={() => navigate('social')}
          working={working}
          notice={publishNotice}
          disabledReason={publishPanelDisabledReason}
        />
      )}
      <div className="post-preview-layout-v2">
        <div className="post-preview-phone-panel-v2">
          <BatchPreview
            posts={previewPost ? [previewPost] : []}
            platforms={platforms}
            targets={previewTargets}
            topic={currentContent?.contentItem?.topic || title}
            batch={currentContent?.generationBatch || null}
            showActions={false}
            showHeader={false}
          />
        </div>
        <aside className="post-preview-side-v2">
          <div className="card post-preview-copy-card-v2">
            <div className="post-preview-card-head-v2">
              <div>
                <div className="eyebrow">{t('misc.caption')}</div>
                <h3 className="h3">{t('misc.postContent')}</h3>
              </div>
              <span className="chip">{t('misc.charsCount', { count: text.length })}</span>
            </div>
            <div className="post-preview-copy-text-v2">{text}</div>
          </div>

          <div className="card post-preview-publish-card-v2">
            <div className="post-preview-card-head-v2">
              <div>
                <div className="eyebrow">{t('misc.publishingLabel')}</div>
                <h3 className="h3">{t('misc.currentTargets')}</h3>
              </div>
              <span className={'post-status-badge compact status-' + lifecycle.status}>{lifecycle.label}</span>
            </div>
            <div className="post-preview-target-list-v2">
              {previewTargetRows(previewTargets, platforms, lifecycle).map(target => (
                <div key={`${target.platform}-${target.id || target.socialAccountId || target.name}`} className="post-preview-target-row-v2">
                  <Platform name={target.platform} size={22} />
                  <div>
                    <div className="post-preview-target-name-v2">{targetDisplayName(target, target.platform)}</div>
                    <div className="caption">{targetCaptionLine(target, target.platform, t)}</div>
                  </div>
                  <span className={'post-target-status-dot status-' + (target.status || lifecycle.status)} />
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
      <div className="post-preview-mobile-bar editor-mobile-bar">
        <button className="btn" onClick={() => openInlinePublishPanel('schedule')} disabled={!canCreatePublish && lifecycle.status !== 'scheduled'} style={{flex:'0 0 40%'}}>{t('misc.schedule')}</button>
        <button className="btn btn-primary" onClick={() => openInlinePublishPanel('publish')} disabled={!canPublishNow || working === 'publish-now'} style={{flex:'0 0 calc(60% - 8px)'}}>{working === 'publish-now' ? t('misc.publishing') : t('misc.publishNow')}</button>
      </div>
      <RepublishDialog
        open={republishOpen}
        content={currentContent}
        onClose={() => setRepublishOpen(false)}
        onComplete={handleRepublishComplete}
      />
      <CreditConfirmationModal
        open={Boolean(creditAction)}
        title={creditAction?.title || 'Confirm plan quota'}
        description={creditActionDescription(creditAction)}
        creditCost={creditAction?.cost || 1}
        creditUsage={creditUsage}
        confirming={creditConfirming || working === 'regenerate'}
        onConfirm={confirmPaidAction}
        onCancel={() => setCreditAction(null)}
        onBuyCredits={() => openUpgrade({ feature: 'quota' })}
        onUpgrade={() => openUpgrade({ feature: 'quota' })}
      />
      <SocialConnectConfirm
        platform={socialConnectPlatform}
        entitlements={dash.entitlements}
        onCancel={() => setSocialConnectPlatform(null)}
        onContinue={(meta) => {
          const platform = socialConnectPlatform;
          setSocialConnectPlatform(null);
          startActivationConnect(platform, meta);
        }}
      />
    </div>
  );
};

function ActivationSocialConnectCard({ platforms, rows, status, telegramLink, onConnect, onSkip, onSchedule }) {
  const { t } = useTranslation();
  const primaryPlatform = rows[0]?.platform || platforms[0] || 'instagram';
  const readyRows = rows.filter(row => row.connected && row.publishReady);
  return (
    <div className="card post-preview-copy-card-v2" style={{ marginBottom: 12 }}>
      <div className="post-preview-card-head-v2">
        <div>
          <div className="eyebrow">{t('misc.activation')}</div>
          <h3 className="h3">{t('misc.firstPostReadyDot')}</h3>
        </div>
        <span className="chip">{t('misc.connectToSchedule')}</span>
      </div>
      <p className="body-sm" style={{ color: 'var(--text-2)', marginTop: 0 }}>
        {t('misc.connectPlatformToScheduleSkip', { platform: platformLabel(primaryPlatform) })}
      </p>
      <div className="post-preview-target-list-v2" style={{ marginTop: 12 }}>
        {rows.map(row => (
          <div key={row.platform} className="post-preview-target-row-v2">
            <Platform name={row.platform} size={22} />
            <div className="grow">
              <div className="post-preview-target-name-v2">{platformLabel(row.platform)}</div>
              <div className="caption">{activationRowCaption(row)}</div>
            </div>
            {row.connected ? (
              <span className="chip chip-green"><span className="dot dot-success" /> {t('misc.connected')}</span>
            ) : row.implemented ? (
              <button className="btn btn-primary btn-sm" onClick={() => onConnect(row.platform)}>{connectLabel(row.platform)}</button>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => onConnect(row.platform)}>{t('misc.comingSoon')}</button>
            )}
          </div>
        ))}
      </div>
      {status && <div className="auth-note" style={{ marginTop: 12 }}><Icon name="info" size={13} /> {status}</div>}
      {telegramLink && (
        <div className="auth-note" style={{ marginTop: 12 }}>
          <Icon name="info" size={13} /> {t('misc.telegramLinkExpiresAt', { time: new Date(telegramLink.expiresAt).toLocaleString() })}
          <a className="btn btn-secondary btn-sm" href={telegramLink.linkUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>{t('misc.openTelegram')}</a>
        </div>
      )}
      <div className="row gap-8" style={{ marginTop: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-sm" disabled={!readyRows.length} onClick={onSchedule}>
          <Icon name="calendar" size={12} /> {t('misc.scheduleFirstPost')}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onSkip}>{t('misc.skipForNow')}</button>
      </div>
    </div>
  );
}

function InlinePublishPanel({ mode, scheduleAt, onScheduleAtChange, targetRows, selectedTargetIds, onToggleTarget, onSubmit, onConnect, working, notice, disabledReason }) {
  const { t } = useTranslation();
  const isPublishNow = mode === 'publish';
  const busy = working === 'publish-now' || working === 'schedule-create';
  const selectedCount = selectedTargetIds.filter(id => targetRows.some(row => row.id === id && !row.blockedReason)).length;
  const actionLabel = isPublishNow ? t('misc.confirmPublishNow') : t('misc.confirmSchedule');
  const title = isPublishNow ? t('misc.publishNow') : t('misc.schedulePost');
  const helper = isPublishNow
    ? t('misc.publishNowHelper')
    : t('misc.scheduleHelper');
  return (
    <section className="card inline-publish-panel" aria-live="polite">
      <div className="inline-publish-head">
        <div>
          <div className="eyebrow">{isPublishNow ? t('misc.immediate') : t('misc.scheduled')}</div>
          <h3 className="h3">{title}</h3>
          <p className="caption">{helper}</p>
        </div>
        <span className="chip">{t('misc.selectedCount', { count: selectedCount })}</span>
      </div>

      {!isPublishNow && (
        <label className="inline-publish-field">
          <span>{t('misc.dateAndTime')} · <DeviceTimezoneLabel compact /></span>
          <input className="input" type="datetime-local" value={scheduleAt} min={toDatetimeLocalValue(new Date(Date.now() + 60 * 1000))} onChange={event => onScheduleAtChange(event.target.value)} />
        </label>
      )}

      <TargetAccountPicker
        rows={targetRows}
        selectedTargetIds={selectedTargetIds}
        onToggle={onToggleTarget}
        onConnect={onConnect}
      />

      {notice && <div className={(notice.type === 'success' ? 'auth-note' : 'auth-error') + ' inline-publish-notice'}><Icon name={notice.type === 'success' ? 'checkCircle' : 'alert'} size={14} /> {notice.message}</div>}
      {disabledReason && !notice && <div className="auth-note inline-publish-notice"><Icon name="info" size={13} /> {disabledReason}</div>}

      <div className="inline-publish-actions">
        <button className="btn btn-primary" onClick={onSubmit} disabled={busy || Boolean(disabledReason)}>
          <Icon name={isPublishNow ? 'send' : 'calendar'} size={14} /> {busy ? (isPublishNow ? t('misc.publishing') : t('misc.scheduling')) : actionLabel}
        </button>
      </div>
    </section>
  );
}

function TargetAccountPicker({ rows, selectedTargetIds, onToggle, onConnect }) {
  const { t } = useTranslation();
  if (!rows.length) {
    return (
      <div className="inline-target-empty">
        <Icon name="plug" size={18} />
        <div>
          <strong>{t('misc.noConnectedAccountForPost')}</strong>
          <p>{t('misc.connectReadyAccountThenReturn')}</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={onConnect}>{t('misc.connectAccount')}</button>
      </div>
    );
  }
  return (
    <div className="inline-target-picker" role="group" aria-label={t('misc.publishTargetAccounts')}>
      {rows.map(row => {
        const selected = selectedTargetIds.includes(row.id) && !row.blockedReason;
        return (
          <button
            key={row.id}
            type="button"
            className={'inline-target-row ' + (selected ? 'is-selected ' : '') + (row.blockedReason ? 'is-disabled' : '')}
            onClick={() => !row.blockedReason && onToggle(row.id)}
            disabled={Boolean(row.blockedReason)}
          >
            <Platform name={row.platform} size={24} />
            <span>
              <strong>{row.name}</strong>
              <small>{row.blockedReason || row.secondaryText || platformLabel(row.platform)}</small>
            </span>
            <Icon name={selected ? 'checkCircle' : row.blockedReason ? 'alert' : 'target'} size={15} />
          </button>
        );
      })}
    </div>
  );
}

function PlatformBadge({ platform, status = 'ready', className = '' }) {
  return (
    <span className={'post-platform-badge status-' + status + ' platform-' + normalizeSocialPlatform(platform) + (className ? ' ' + className : '')} role="listitem" aria-label={platformLabel(platform)}>
      <span className="post-platform-logo"><Platform name={platform} size={28} /></span>
      <span>{platformLabel(platform)}</span>
    </span>
  );
}

function readPostPreviewIntent(contentId) {
  if (!contentId || typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(POST_PREVIEW_INTENT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.contentId !== contentId) return null;
    window.sessionStorage.removeItem(POST_PREVIEW_INTENT_KEY);
    return parsed.mode === 'publish' || parsed.mode === 'schedule' ? parsed.mode : null;
  } catch {
    window.sessionStorage.removeItem(POST_PREVIEW_INTENT_KEY);
    return null;
  }
}

function FirstPostActivationOverview({ details, hasImage, lifecycle, onGenerateImage, imageRetrying }) {
  const { t } = useTranslation();
  return (
    <section className="card first-post-activation-panel">
      <div className="post-preview-card-head-v2">
        <div>
          <div className="eyebrow">{t('misc.activationPreview')}</div>
          <h3 className="h3">{t('misc.firstPostReady')}</h3>
        </div>
        <span className={'post-status-badge compact status-' + lifecycle.status}>{lifecycle.label}</span>
      </div>
      <div className="first-post-activation-grid">
        <ActivationField label={t('misc.fieldPlatform')} value={platformLabel(details.platform)} />
        <ActivationField label={t('misc.fieldFormat')} value={formatLabel(details.format)} />
        <ActivationField label={t('misc.fieldImage')} value={hasImage ? t('misc.generatedImageReady') : details.imageWarning ? t('misc.textReadyImageFailed') : t('misc.imagePlaceholder')} action={!hasImage ? <button className="btn btn-secondary btn-sm" onClick={onGenerateImage} disabled={imageRetrying}>{imageRetrying ? t('misc.generatingEllipsis') : t('misc.generateImage')}</button> : null} />
        <ActivationField label={t('misc.fieldHook')} value={details.hook} wide />
        <ActivationField label={t('misc.fieldCaptionBody')} value={details.caption || details.body} wide prewrap />
        <ActivationField label={t('misc.fieldCta')} value={details.cta || t('misc.noCtaReturned')} />
        <ActivationField label={t('misc.fieldHashtags')} value={details.hashtags.length ? details.hashtags.join(' ') : t('misc.noneForThisPlatform')} />
        <ActivationField label={t('misc.fieldWhyChosen')} value={details.whyThisFirst} wide />
        <ActivationField label={t('misc.fieldStrategyContext')} value={details.strategyContext} wide />
      </div>
    </section>
  );
}

function ActivationField({ label, value, action = null, wide = false, prewrap = false }) {
  const { t } = useTranslation();
  return (
    <div className={'activation-field ' + (wide ? 'wide ' : '') + (prewrap ? 'prewrap' : '')}>
      <span>{label}</span>
      <strong>{value || t('misc.pending')}</strong>
      {action}
    </div>
  );
}

function activationConnectRows(platforms, accounts, registry) {
  return normalizeTargetPlatforms(platforms).map(platform => {
    const account = accounts.find(item => normalizeSocialPlatform(item.platform) === platform) || null;
    const blockedReason = account ? publishTargetBlockedReason(account, registry) : null;
    return {
      platform,
      account,
      connected: Boolean(account && account.status === 'connected'),
      publishReady: Boolean(account && !blockedReason),
      blockedReason,
      implemented: activationProviderImplemented(platform, registry[platform]),
      registry: registry[platform]
    };
  });
}

function activationProviderImplemented(platform, registryItem) {
  if (platform === 'telegram') return true;
  if (!['instagram', 'threads', 'facebook', 'tiktok'].includes(platform)) return false;
  return registryItem ? registryItem.supportsOauth !== false : true;
}

function activationRowCaption(row) {
  if (row.connected && row.publishReady) return row.account ? `Ready: ${displaySocialAccountName(row.account, { label: platformLabel(row.platform) })}` : 'Ready to schedule';
  if (row.connected && row.blockedReason) return row.blockedReason;
  if (!row.implemented) return 'Coming soon. Connect later from Settings.';
  if (row.platform === 'telegram') return 'Create a bot setup link and add it to a channel.';
  return 'Not connected yet.';
}

function mergePreviewContent(feedContent, selectedContent) {
  if (!feedContent) return selectedContent;
  if (!selectedContent) return feedContent;
  const mediaSource = loadedContentMediaCount(selectedContent) > loadedContentMediaCount(feedContent) ? selectedContent : feedContent;
  return {
    ...selectedContent,
    ...feedContent,
    contentItem: { ...(selectedContent.contentItem || {}), ...(feedContent.contentItem || {}) },
    activeVersion: {
      ...(selectedContent.activeVersion || {}),
      ...(feedContent.activeVersion || {}),
      mediaUrls: mediaSource.activeVersion?.mediaUrls || mediaSource.mediaUrls || [],
      mediaPreview: mergePreviewMedia(feedContent.activeVersion?.mediaPreview, selectedContent.activeVersion?.mediaPreview || mediaSource.activeVersion?.mediaPreview),
    },
    carouselSlides: mediaSource.carouselSlides || [],
    mediaUrls: mediaSource.mediaUrls || mediaSource.activeVersion?.mediaUrls || [],
    mediaPreview: mergePreviewMedia(feedContent.mediaPreview, selectedContent.mediaPreview || mediaSource.mediaPreview),
    generationBatch: feedContent.generationBatch || selectedContent.generationBatch || null,
    publishSummary: feedContent.publishSummary || selectedContent.publishSummary || null,
    publishPlatforms: feedContent.publishPlatforms || selectedContent.publishPlatforms,
    publishTargetAccountIds: feedContent.publishTargetAccountIds || selectedContent.publishTargetAccountIds,
    publishMode: feedContent.publishMode || selectedContent.publishMode,
    publishScheduleAt: feedContent.publishScheduleAt || selectedContent.publishScheduleAt,
    firstPost: selectedContent.firstPost || feedContent.firstPost || firstPostActivationMeta(feedContent) || firstPostActivationMeta(selectedContent) || null,
  };
}

function loadedContentMediaCount(content) {
  return Math.max(
    Array.isArray(content?.carouselSlides) ? content.carouselSlides.length : 0,
    Array.isArray(content?.mediaUrls) ? content.mediaUrls.length : 0,
    Array.isArray(content?.activeVersion?.mediaUrls) ? content.activeVersion.mediaUrls.length : 0
  );
}

function mergePreviewMedia(base, next) {
  return {
    ...(base || {}),
    ...(next || {}),
    count: Math.max(Number(base?.count || 0), Number(next?.count || 0)),
    hasCarousel: Boolean(base?.hasCarousel || next?.hasCarousel || Number(base?.count || 0) > 1 || Number(next?.count || 0) > 1),
  };
}

function PlatformPreview({ platform, target, text }) {
  const { t } = useTranslation();
  const title = targetDisplayName(target, platform);
  const avatarText = initials(title);
  const meta = platform === 'telegram'
    ? targetMetaLine(target, platform) || t('misc.telegramChannel')
    : targetMetaLine(target, platform) || target?.secondaryText || t('misc.platformAccount', { platform: platformLabel(platform) });
  const actions = platform === 'telegram'
    ? [['eye', t('misc.viewsAfterPublish')], ['send', t('misc.share')], ['msg', t('misc.channelPost')]]
    : [['heart', t('misc.like')], ['msg', t('misc.comment')], ['send', t('misc.share')]];

  return (
    <div className={'platform-preview-phone platform-preview-' + platform}>
      <div className="platform-preview-notch" />
      <div className="li-preview platform-preview-screen">
        <div className="platform-preview-head">
          <div className="avatar" style={{width:40, height:40}}>{avatarText}</div>
          <div style={{ minWidth: 0 }}>
            <div className="platform-preview-title">{title}</div>
            <div className="muted platform-preview-meta">{meta}</div>
            <div className="muted platform-preview-meta">{t('misc.justNow')}</div>
          </div>
        </div>
        <div style={{whiteSpace:'pre-wrap', fontSize:13.5, lineHeight:1.55}}>{text}</div>
        <div className="li-actions">
          {actions.map(([icon, label]) => <span key={label}><Icon name={icon} size={14}/> {label}</span>)}
        </div>
      </div>
    </div>
  );
}

function GenerationProgressCard({ content }) {
  const { t } = useTranslation();
  const batch = content?.generationBatch || null;
  const progress = clampProgress(content?.contentItem?.generationProgress ?? batch?.progress ?? 5);
  const ready = Number(batch?.readyCount ?? 0);
  const total = Number(batch?.totalItems ?? 1);
  const label = batch ? t('misc.readyOfTotal', { ready, total }) : t('misc.percentReady', { percent: progress });
  return (
    <div className="auth-note generation-progress-card">
      <div className="row between" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 8 }}><Icon name="sparkles" size={14} /> <span>{t('misc.aiWritingCarousel')}</span></div>
        <span className="caption">{label}</span>
      </div>
      <div className="dt-progress" style={{ marginTop: 10 }}><div className="dt-progress-fill" style={{ width: `${progress}%` }} /></div>
    </div>
  );
}

// Per-account language chip. Two states:
//  - Entitled (Growth+): renders a native <select> with the language code +
//    a small caret. Compact, lives in the row actions next to the status pill.
//  - Locked (free/starter): renders a static chip with a lock icon; whole
//    chip is clickable and routes to billing. Same footprint so the row
//    layout stays calm.
function AccountLanguageChip({ account, canOverride, isPosting, saving, onChange, onUpgrade }) {
  const { t } = useTranslation();
  const code = (account.publishLanguage || '').toLowerCase() || null;
  const label = code ? code.toUpperCase() : t('misc.languageDefault');
  const fullLabel = code ? publishLanguageLabel(code) : t('misc.orgDefault');
  // TASK-315: Scheduler states the fact as a quiet pill — no click-through to
  // billing (unlike the free/starter locked chip below, which upsells).
  if (!canOverride && isPosting) {
    return (
      <span
        className="chip chip-soft account-lang-chip is-static"
        title={t('misc.languagePostingTagTitle')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Icon name="globe" size={11} />
        <span>{t('misc.languagePostingTag')}</span>
      </span>
    );
  }
  if (!canOverride) {
    return (
      <button
        type="button"
        className="chip chip-soft account-lang-chip is-locked"
        onClick={onUpgrade}
        title={t('misc.perAccountLanguageGrowthFeature')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Icon name="lock" size={10} />
        <span>{label}</span>
      </button>
    );
  }
  return (
    <label
      className="chip account-lang-chip is-editable"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
      title={t('misc.postsToLanguage', { language: fullLabel })}
    >
      <Icon name="globe" size={11} />
      <span>{label}</span>
      <select
        value={code || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={saving}
        style={{
          position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer',
          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
        }}
        aria-label={t('misc.publishLanguage')}
      >
        <option value="">{t('misc.orgDefault')}</option>
        {PUBLISH_LANGUAGES.map(l => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
    </label>
  );
}

// SOCIAL ACCOUNTS
export const SocialAccounts = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dash = useDashboardData() || {};
  // Per-account language override is a Growth+ feature. Lower plans (free
  // + starter) only get the org-wide default set in /app/plan/edit-strategy.
  const canOverrideLanguage = Boolean(dash.entitlements?.features?.per_account_language_override);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [accountError, setAccountError] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [telegramLink, setTelegramLink] = useState(null);
  const [requestedConnections, setRequestedConnections] = useState([]);
  const [disconnectingAccountId, setDisconnectingAccountId] = useState(null);
  const [activeHelper, setActiveHelper] = useState(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState([]);
  const [languageSavingId, setLanguageSavingId] = useState(null);
  const [oauthResult] = useState(readOAuthResult);
  const [platformRegistry, setPlatformRegistry] = useState({});
  const [socialConnectPlatform, setSocialConnectPlatform] = useState(null);
  const catalog = buildSocialCatalog(platformRegistry);

  const handleLanguageChange = async (accountId, language) => {
    if (!canOverrideLanguage) {
      navigate('/app/settings?tab=billing');
      return;
    }
    setLanguageSavingId(accountId);
    setAccountError(null);
    // Optimistic update: flip the chip immediately so users feel snappy.
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, publishLanguage: language || null } : a));
    try {
      await api.socialAccounts.updateLanguage(accountId, language || null);
      // Refresh the dashboard cache so /app/create sees the new language
      // on the next render — without this CreatePost would keep showing
      // the old chip until the 2-minute TTL expires.
      dash.invalidateResource?.('socialAccounts');
    } catch (error) {
      // 402 ENTITLEMENT_REQUIRED → push to billing. Other errors → roll back + show error.
      const code = error?.body?.error?.code || error?.data?.error?.code;
      if (code === 'ENTITLEMENT_REQUIRED') {
        navigate('/app/settings?tab=billing');
        return;
      }
      setAccountError(friendlyErrorMessage(error) || t('misc.couldNotUpdateLanguage'));
      // Roll back to whatever the server returns next.
      loadAccounts({ silent: true }).catch(() => {});
    } finally {
      setLanguageSavingId(null);
    }
  };

  const loadAccounts = async (options = {}) => {
    const isCancelled = options.isCancelled || (() => false);
    try {
      if (!options.silent) {
        setLoadingAccounts(true);
      }
      setAccountError(null);
      const response = await api.socialAccounts.list();
      if (!isCancelled()) {
        setAccounts(response.accounts || []);
      }
    } catch (error) {
      if (!isCancelled()) {
        setAccountError(error.message || t('misc.unableToLoadConnectedAccounts'));
      }
    } finally {
      if (!isCancelled()) {
        setLoadingAccounts(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    dash.ensureDashboardData?.().catch(() => {});
    loadAccounts({ isCancelled: () => cancelled });
    api.platforms.registry()
      .then(response => {
        if (!cancelled) {
          setPlatformRegistry(Object.fromEntries((response.platforms || []).map(item => [normalizeSocialPlatform(item.platform), item])));
        }
      })
      .catch(() => {
        if (!cancelled) setPlatformRegistry({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-expand every provider that already has accounts — the language chip
  // + per-account controls live in the expanded body, so collapsing-by-default
  // hides the new picker from view. Users can still toggle expansion.
  useEffect(() => {
    if (!accounts.length) return;
    const platformsWithAccounts = Array.from(new Set(accounts.map(a => normalizeSocialPlatform(a.platform)).filter(Boolean)));
    setExpandedPlatforms(prev => {
      const merged = new Set([...prev, ...platformsWithAccounts]);
      return Array.from(merged);
    });
  }, [accounts]);

  useEffect(() => {
    if (oauthResult && typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/social');
    }
  }, [oauthResult]);

  const catalogByPlatform = new Map();
  catalog.forEach(item => {
    const catalogItem = socialCatalogItem(item);
    catalogByPlatform.set(normalizeSocialPlatform(item.p), catalogItem);
    catalogByPlatform.set(item.p, catalogItem);
  });

  const connectedRows = accounts.map(account => {
    const platform = normalizeSocialPlatform(account.platform);
    const catalogItem = catalogByPlatform.get(platform) || fallbackCatalogItem(platform);
    const displayName = displaySocialAccountName(account, catalogItem);
    return {
      ...catalogItem,
      p: platform,
      backendAccountId: account.id,
      externalAccountId: account.externalAccountId,
      name: displayName,
      secondaryText: socialAccountSecondaryText(account, displayName),
      status: mapBackendStatus(account.status),
      // TASK-329 round 3 fix (BLOCKER): mapBackendStatus collapses expired/
      // revoked/failed into one 'reconnect' UI status for the pill/count —
      // the reconnect button below needs the RAW backend status back to
      // tell a re-auth of an already-connected row (needs_reconnect/
      // expired/failed) apart from bringing back a genuinely revoked/
      // disconnected one (isReauthOfConnectedAccount, socialAddon.js).
      rawStatus: account.status,
      connectedAt: account.connectedAt,
      publishLanguage: account.publishLanguage || null,
      permissions: describeCapabilities(account.capabilities, catalogItem.permissions, platformRegistry[platform]),
      registry: platformRegistry[platform],
      hasBackendAccount: true
    };
  });
  const accountRowsByPlatform = connectedRows.reduce((map, account) => {
    const rows = map.get(account.p) || [];
    rows.push(account);
    map.set(account.p, rows);
    return map;
  }, new Map());
  const providerRows = catalog
    .map(socialCatalogItem)
    .filter(item => isConnectableSocialPlatform(item.p, platformRegistry[normalizeSocialPlatform(item.p)]))
    .map(item => ({
      ...item,
      p: normalizeSocialPlatform(item.p),
      accounts: accountRowsByPlatform.get(normalizeSocialPlatform(item.p)) || [],
      auto: false,
      registry: platformRegistry[normalizeSocialPlatform(item.p)],
      hasBackendAccount: false
    }));
  const connectableAvailableRows = providerRows;
  const connectedCount = connectedRows.filter(a => a.status === 'connected').length;
  const reconnectCount = connectedRows.filter(a => a.status === 'reconnect').length;
  const hasRequestedConnection = platform => requestedConnections.includes(normalizeSocialPlatform(platform));
  const showFacebookHelper = activeHelper === 'facebook' || oauthResult?.error === 'no_facebook_pages';
  const showTelegramHelper = activeHelper === 'telegram' || Boolean(telegramLink);
  const statusMeta = {
    connected: { label: t('misc.connected'), chip: 'chip chip-green', dot: 'dot dot-success' },
    reconnect: { label: t('misc.authRequired'), chip: 'chip chip-amber', dot: 'dot dot-warn' },
    'not-connected': { label: t('misc.notConnected'), chip: 'chip', dot: 'dot dot-subtle' },
  };
  const showInstagramFallback = !accountRowsByPlatform.has('instagram') || activeHelper === 'instagram-facebook' || oauthResult?.error === 'no_instagram_business_accounts';

  const createPlaceholder = (platform) => {
    setRequestedConnections(prev => prev.includes(platform) ? prev : [...prev, platform]);
  };

  const togglePlatformAccounts = (platform) => {
    const normalizedPlatform = normalizeSocialPlatform(platform);
    setExpandedPlatforms(prev => prev.includes(normalizedPlatform)
      ? prev.filter(item => item !== normalizedPlatform)
      : [...prev, normalizedPlatform]);
  };

  const focusQuickAdd = () => {
    if (typeof document === 'undefined') return;
    document.getElementById('social-quick-add')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const startConnectPlatform = async (platform, meta = {}) => {
    const normalizedPlatform = normalizeSocialPlatform(platform);
    setActionStatus(null);
    setTelegramLink(null);
    setActiveHelper(normalizedPlatform);
    if (normalizedPlatform === 'instagram' || normalizedPlatform === 'threads' || normalizedPlatform === 'facebook' || normalizedPlatform === 'tiktok' || normalizedPlatform === 'x') {
      try {
        setActionStatus(t('misc.redirectingToOauth', { platform: platformLabel(normalizedPlatform) }));
        // TASK-329 round 4 fix (guard contract, 2nd review pass): overageAck
        // is now sent on EVERY <SocialConnectConfirm> Continue (see that
        // component's own comment) — this still only forwards a caller-
        // supplied meta.overageAck rather than hardcoding true here, because
        // this same function also serves the plain reconnect button below,
        // which never opens that confirm sheet at all and must never claim a
        // price was acknowledged that was never shown. reconnect: true marks
        // a re-auth of an account the org already has connected (see
        // isReauthOfConnectedAccount, socialAddon.js) — the auth-worker
        // exempts THAT case from the ack requirement instead.
        await api.socialAccounts.connect(normalizedPlatform, {
          overageAck: meta?.overageAck === true,
          reconnect: meta?.reconnect === true,
        });
      } catch (error) {
        setActionStatus(error.message || t('misc.unableToStartOauth', { platform: platformLabel(normalizedPlatform) }));
      }
      return;
    }
    if (normalizedPlatform === 'telegram') {
      try {
        setActionStatus(t('misc.creatingTelegramLink'));
        const response = await api.socialAccounts.createTelegramLink();
        setTelegramLink(response);
        setActionStatus(t('misc.telegramSetupReadyAddChannel'));
      } catch (error) {
        setActionStatus(error.message || t('misc.unableToCreateTelegramLink'));
      }
      return;
    }
    createPlaceholder(normalizedPlatform);
    setActionStatus(t('misc.platformComingSoonSetup', { platform: platformLabel(normalizedPlatform) }));
  };

  // TASK-329: same generalised gate as Settings.jsx AccountsTab's
  // handleConnect — a billable org over its plan's included social-account
  // count gets the confirm dialog too, not just the Scheduler.
  const connectPlatform = (platform) => {
    const normalizedPlatform = normalizeSocialPlatform(platform);
    const social = dash.entitlements?.socialAccounts;
    if (dash.entitlements?.plan === 'posting' || wouldExceedIncluded(social)) {
      setSocialConnectPlatform(normalizedPlatform);
      return;
    }
    startConnectPlatform(normalizedPlatform);
  };

  // Instagram-via-Facebook was removed product-wide. This legacy screen's entry
  // points now route to the DIRECT Instagram Login flow (no Facebook), so nothing
  // here can start a via-Facebook Instagram connect.
  const connectInstagramViaFacebook = async () => {
    connectPlatform('instagram');
  };

  const disconnectAccount = async (account) => {
    if (!account.backendAccountId) return;
    // TASK-329: an overage disconnect (this account is one of the billed
    // `extra`) shows the bill-drop copy — mirrors Settings.jsx AccountsTab's
    // handleDisconnect exactly.
    const social = dash.entitlements?.socialAccounts;
    const socialAddonState = resolveSocialAddonState(social);
    const isOverageDisconnect = socialAddonState.mode === 'priced' && socialAddonState.extra > 0;
    const confirmCopy = dash.entitlements?.plan === 'posting'
      ? buildPostingDisconnectCopy({ label: account.name, currentQuantity: countConnectedSocials(accounts), t }).message
      : isOverageDisconnect
        ? buildSocialAddonDisconnectCopy({
            label: account.name,
            connected: socialAddonState.connected,
            included: socialAddonState.included,
            unitAmount: socialAddonState.unitAmount,
            currency: socialAddonState.currency,
            locale: i18n.language || 'en',
            t,
          }).message
        : t('misc.disconnectConfirm', { name: account.name });
    if (dash.entitlements?.plan === 'posting') {
      try { track('posting_disconnect_confirm_shown', { accountId: account.backendAccountId }); } catch (_) { /* fail-silent */ }
    } else if (isOverageDisconnect) {
      try { track('social_addon_disconnect_confirm_shown', { accountId: account.backendAccountId }); } catch (_) { /* fail-silent */ }
    }
    if (typeof window !== 'undefined' && !window.confirm(confirmCopy)) {
      return;
    }
    try {
      setDisconnectingAccountId(account.backendAccountId);
      setActionStatus(null);
      await api.socialAccounts.disconnect(account.backendAccountId);
      setActionStatus(t('misc.accountDisconnected', { name: account.name }));
      await loadAccounts({ silent: true });
    } catch (error) {
      setActionStatus(error.message || t('misc.unableToDisconnect', { name: account.name }));
    } finally {
      setDisconnectingAccountId(null);
    }
  };

  return (
    <div className="page social-page social-redesign social-accounts-simple">
      <header className="social-simple-header">
        <div>
          <div className="eyebrow">{t('misc.settings')}</div>
          <h1>{t('misc.socialAccounts')}</h1>
          <p>{t('misc.socialAccountsIntro')}</p>
        </div>
        <div className="social-header-actions">
          <div className="social-header-stats" aria-label={t('misc.socialAccountSummary')}>
            <span>{t('misc.statReady', { value: loadingAccounts ? '...' : connectedCount })}</span>
            <span>{t('misc.statNeedAuth', { value: loadingAccounts ? '...' : reconnectCount })}</span>
            <span>{t('misc.statProviders', { value: loadingAccounts ? '...' : connectableAvailableRows.length })}</span>
          </div>
          <button className="btn btn-primary" onClick={focusQuickAdd} disabled={!connectableAvailableRows.length}>
            <Icon name="plus" size={15}/> {t('misc.connectAccount')}
          </button>
          <button className="btn btn-secondary" onClick={() => loadAccounts({ silent: true })} disabled={loadingAccounts}>
            <Icon name="refresh" size={15}/> {t('misc.refresh')}
          </button>
        </div>
      </header>

      <div className="social-content-stack">
        {(oauthResult || accountError || actionStatus || telegramLink) && (
          <div className={'card social-oauth-banner ' + ((oauthResult?.error || accountError) ? 'is-error' : 'is-success')}>
            <div className="type-icon"><Icon name={(oauthResult?.error || accountError) ? 'alert' : 'info'} size={18}/></div>
            <div className="grow">
              {(oauthResult || accountError) && <h3 className="h3">{accountError ? t('misc.connectedAccountsUnavailable') : oauthResultTitle(oauthResult)}</h3>}
              {(oauthResult || accountError) && <p className="body-sm">{accountError ? accountError : oauthResultMessage(oauthResult)}</p>}
              {actionStatus && <p className="body-sm">{actionStatus}</p>}
              {telegramLink && (
                <div className="telegram-link-box">
                  <span>{t('misc.telegramSetupExpiresAt', { time: new Date(telegramLink.expiresAt).toLocaleString() })}</span>
                  <a className="btn btn-primary btn-sm" href={telegramLink.linkUrl} target="_blank" rel="noreferrer">{t('misc.openTelegram')}</a>
                </div>
              )}
              {(oauthResult?.error === 'no_facebook_pages' || oauthResult?.error === 'no_instagram_business_accounts') && (
                <div className="social-banner-actions">
                  <a className="btn btn-secondary btn-sm" href="https://www.facebook.com/pages/create" target="_blank" rel="noreferrer">{t('misc.createFacebookPage')}</a>
                  <button className="btn btn-primary btn-sm" onClick={connectInstagramViaFacebook}>{t('misc.recheck')}</button>
                </div>
              )}
            </div>
          </div>
        )}

        <section id="social-quick-add" className="card social-section-card">
          <div className="social-card-head">
            <div>
              <h3 className="h3">{t('misc.accountsByNetwork')}</h3>
              <p className="caption">{loadingAccounts ? t('misc.checkingAccounts') : t('misc.accountsReadySummary', { accounts: t('misc.accountCount', { count: connectedRows.length }), ready: connectedCount })}</p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => loadAccounts({ silent: true })} disabled={loadingAccounts}>
              <Icon name="refresh" size={14}/> {t('misc.refresh')}
            </button>
          </div>

          {loadingAccounts ? (
            <div className="social-provider-grid">
              {[0, 1, 2].map(index => (
                <div className="social-provider-card social-loading-card" key={index}>
                  <div className="social-skeleton-avatar shimmer" />
                  <div className="social-skeleton-lines">
                    <span className="shimmer" />
                    <span className="shimmer short" />
                  </div>
                </div>
              ))}
            </div>
          ) : providerRows.length ? (
            <div className="social-provider-grid">
              {providerRows.map((provider) => {
                const providerAccounts = provider.accounts || [];
                const readyCount = providerAccounts.filter(account => account.status === 'connected').length;
                const reconnectingCount = providerAccounts.filter(account => account.status === 'reconnect').length;
                const expanded = expandedPlatforms.includes(provider.p);
                const statusCopy = providerAccounts.length
                  ? `${t('misc.accountCount', { count: providerAccounts.length })} · ${t('misc.countReady', { count: readyCount })}${reconnectingCount ? ` · ${t('misc.countNeedAuth', { count: reconnectingCount })}` : ''}`
                  : t('misc.noAccountConnected');
                return (
                  <article key={provider.p} className={'social-provider-card ' + (providerAccounts.length ? 'has-accounts' : 'is-empty')}>
                    <div className="social-provider-summary">
                      <div className="social-provider-main">
                        <Platform name={provider.p} size={42}/>
                        <div>
                          <h4>{provider.label}</h4>
                          <p>{statusCopy}</p>
                        </div>
                      </div>
                      <div className="social-provider-actions">
                        <button className="btn btn-primary btn-sm" onClick={() => connectPlatform(provider.p)}>
                          <Icon name="plus" size={13}/> {providerAccounts.length ? addAnotherLabel(provider.p) : connectLabel(provider.p)}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => togglePlatformAccounts(provider.p)} disabled={!providerAccounts.length} aria-expanded={expanded}>
                          {expanded ? t('misc.hideAccounts') : providerAccounts.length ? t('misc.viewAccounts') : t('misc.noAccountsYet')}
                        </button>
                      </div>
                    </div>
                    {expanded && providerAccounts.length > 0 && (
                      <div className="social-provider-body">
                        {providerAccounts.map((account) => {
                          const meta = statusMeta[account.status];
                          const visiblePermissions = account.permissions.slice(0, 4);
                          const hiddenPermissionCount = Math.max(0, account.permissions.length - visiblePermissions.length);
                          return (
                            <div key={account.backendAccountId} className={'social-account-row is-' + account.status}>
                              <div className="social-account-row-main">
                                <h4 className="social-account-name">{account.name}</h4>
                                <p>{account.secondaryText || connectedCaption(account)}</p>
                                <div className="social-permissions">
                                  {visiblePermissions.map(permission => <span key={permission}>{permission}</span>)}
                                  {hiddenPermissionCount > 0 && <span>+{hiddenPermissionCount}</span>}
                                </div>
                              </div>
                              <div className="social-account-row-actions">
                                <span className={meta.chip}><span className={meta.dot}/> {meta.label}</span>
                                {/* Per-account publish language. Click-through to billing on lower plans. */}
                                <AccountLanguageChip
                                  account={account}
                                  canOverride={canOverrideLanguage}
                                  isPosting={dash.entitlements?.plan === 'posting'}
                                  saving={languageSavingId === account.backendAccountId}
                                  onChange={(lang) => handleLanguageChange(account.backendAccountId, lang)}
                                  onUpgrade={() => navigate('/app/settings?tab=billing')}
                                />
                                {account.status === 'reconnect' && (
                                  isReauthOfConnectedAccount(account.rawStatus) ? (
                                    // TASK-329 round 3 fix (BLOCKER): re-
                                    // authorization does not add a billed
                                    // social — no quote. This row's raw
                                    // status is needs_reconnect/expired/
                                    // failed, so the org already counts it
                                    // toward its connected total; routing it
                                    // through connectPlatform's gate (as
                                    // round 2 mistakenly did for every
                                    // mapped 'reconnect' row) could pop
                                    // SocialConnectConfirm/the cap dialog
                                    // for a plain re-auth the backend itself
                                    // never gates.
                                    //
                                    // TASK-329 round 4 fix (guard contract,
                                    // 2nd review pass): also pass
                                    // reconnect: true through to
                                    // api.socialAccounts.connect — the
                                    // auth-worker guard exempts the ack
                                    // requirement for `reconnect=1` ONLY when
                                    // the org already has a row for this
                                    // platform, which is exactly this branch.
                                    <button className="btn btn-primary btn-sm" onClick={() => startConnectPlatform(account.p, { reconnect: true })}>{t('misc.reconnect')}</button>
                                  ) : (
                                    // TASK-329 (CONTRACT UPDATE, slice B r2):
                                    // a genuinely revoked/disconnected
                                    // account's reconnect IS gated like a
                                    // brand-new connect — it no longer
                                    // counts toward the org's connected
                                    // total, so bringing it back can push
                                    // the org back over its included count.
                                    <button className="btn btn-primary btn-sm" onClick={() => connectPlatform(account.p)}>{t('misc.reconnect')}</button>
                                  )
                                )}
                                <button className="btn btn-secondary btn-sm btn-danger-soft" disabled={disconnectingAccountId === account.backendAccountId} onClick={() => disconnectAccount(account)}>
                                  {disconnectingAccountId === account.backendAccountId ? t('misc.disconnecting') : t('misc.disconnect')}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="social-empty-state">
              <div>
                <h3>{t('misc.noActiveConnectionFlows')}</h3>
                <p>{t('misc.platformSetupAppearsHere')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="card social-section-card">
          <div className="social-card-head">
            <div>
              <h3 className="h3">{t('misc.quickAdd')}</h3>
              <p className="caption">{t('misc.quickAddHint')}</p>
            </div>
          </div>
          <div className="social-add-grid">
            {connectableAvailableRows.length ? connectableAvailableRows.map(platform => (
              <button key={platform.p} className={'social-add-card ' + (hasRequestedConnection(platform.p) ? 'is-pending' : '')} onClick={() => connectPlatform(platform.p)}>
                <Platform name={platform.p} size={34}/>
                <div>
                  <h4>{platform.label}</h4>
                  <p>{hasRequestedConnection(platform.p) ? t('misc.setupRequestSaved') : quickAddDescription(platform)}</p>
                </div>
                <span>{platform.accounts?.length ? addAnotherLabel(platform.p) : connectLabel(platform.p)}</span>
              </button>
            )) : (
              <div className="social-empty-state compact">
                <div>
                  <h3>{t('misc.noActiveConnectFlows')}</h3>
                  <p>{t('misc.providerSetupAppearsHere')}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {(showFacebookHelper || showInstagramFallback || showTelegramHelper) && (
          <section className="social-helper-grid">
            {showFacebookHelper && (
              <div className="card social-compact-helper">
                <Platform name="facebook" size={32}/>
                <div>
                  <h3>{t('misc.facebookPageRequired')}</h3>
                  <p>{t('misc.facebookPageRequiredHint')}</p>
                </div>
                <a className="btn btn-secondary btn-sm" href="https://www.facebook.com/pages/create" target="_blank" rel="noreferrer">{t('misc.createPage')}</a>
              </div>
            )}
            {showInstagramFallback && (
              <div className="card social-compact-helper">
                <Platform name="instagram" size={32}/>
                <div>
                  <h3>{t('misc.instagramViaFacebookPage')}</h3>
                  <p>{t('misc.instagramViaFacebookHint')}</p>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={connectInstagramViaFacebook}>{t('misc.connectViaFacebook')}</button>
              </div>
            )}
            {showTelegramHelper && (
              <div className="card social-compact-helper">
                <Platform name="telegram" size={32}/>
                <div>
                  <h3>{t('misc.telegramChannelSetup')}</h3>
                  <p>{t('misc.telegramChannelSetupHint')}</p>
                </div>
              </div>
            )}
          </section>
        )}
        <SocialConnectConfirm
          platform={socialConnectPlatform}
          entitlements={dash.entitlements}
          onCancel={() => setSocialConnectPlatform(null)}
          onContinue={(meta) => {
            const platform = socialConnectPlatform;
            setSocialConnectPlatform(null);
            startConnectPlatform(platform, meta);
          }}
        />
      </div>
    </div>
  );
};

function contentText(content) {
  const version = content?.activeVersion;
  if (!version) return '';
  const platformText = Object.entries(version.platformVersions || {})
    .filter(([key, value]) => !String(key).startsWith('__') && typeof value === 'string' && value.trim())
    .map(([, value]) => value.trim())[0];
  return version.caption || version.body || version.hook || platformText || '';
}

function firstPostActivationMeta(content) {
  const explicit = content?.firstPost;
  if (explicit && typeof explicit === 'object') return explicit;
  const saved = content?.activeVersion?.platformVersions?.__firstPostMeta;
  if (!saved || typeof saved !== 'object') return null;
  return {
    ...saved,
    whyThisFirst: saved.whyThisFirst || saved.explanation,
    strategyContext: saved.strategyContext || saved.activationSummary || saved.angle,
  };
}

function firstPostActivationDetails(content, fallbackPlatform, firstPostInput = null) {
  const firstPost = firstPostInput || firstPostActivationMeta(content) || {};
  const version = content?.activeVersion || {};
  const platform = normalizeSocialPlatform(firstPost.platform || fallbackPlatform || contentPlatforms(content)[0] || 'instagram');
  return {
    platform,
    format: firstPost.format || content?.contentItem?.format || 'image_post',
    topic: firstPost.topic || content?.contentItem?.topic || '',
    hook: firstPost.hook || version.hook || postPreviewTitle(content),
    body: firstPost.body || version.body || '',
    caption: firstPost.caption || platformCaptionFromVersion(version, platform) || contentText(content),
    cta: firstPost.cta || version.cta || '',
    hashtags: Array.isArray(firstPost.hashtags) && firstPost.hashtags.length ? firstPost.hashtags : Array.isArray(version.hashtags) ? version.hashtags : [],
    whyThisFirst: firstPost.whyThisFirst || firstPost.explanation || 'Chosen from your Brand Profile and activation strategy.',
    strategyContext: firstPost.activationSummary || firstPost.strategyContext || firstPost.angle || 'Activation strategy picked one first post to validate the offer before generating a full week.',
    imageWarning: firstPost.imageWarning || null,
  };
}

function platformCaptionFromVersion(version, platform) {
  const value = version?.platformVersions?.[platform];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function summarizeWhyThis(text) {
  const raw = String(text || '').trim();
  if (!raw) return 'This post introduces your offer to your audience and starts the activation journey.';
  const cleaned = raw
    .replace(/\b[a-z]+(?:_[a-z0-9]+){2,}\b/g, token => token.replace(/_/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length <= 280) return cleaned;
  const sentences = cleaned.match(/[^.!?]+[.!?]+/g) || [cleaned];
  const trimmed = sentences.slice(0, 3).join(' ').trim();
  // Hard ceiling so unbroken tokens can never balloon the side card.
  const capped = trimmed.length <= 320 ? trimmed : trimmed.slice(0, 280);
  if (capped.length === trimmed.length) return capped;
  const boundary = capped.lastIndexOf(' ');
  return (boundary > 200 ? capped.slice(0, boundary) : capped).trim() + '…';
}

function hasContentImage(content, previewPost) {
  return Boolean(
    content?.mediaPreview?.url ||
    content?.activeVersion?.mediaPreview?.url ||
    content?.imageUrl ||
    content?.activeVersion?.imageUrl ||
    (Array.isArray(content?.mediaUrls) && content.mediaUrls.length) ||
    (Array.isArray(content?.activeVersion?.mediaUrls) && content.activeVersion.mediaUrls.length) ||
    (Array.isArray(previewPost?.mediaAssets) && previewPost.mediaAssets.some(asset => asset?.publicUrl || asset?.imageUrl || asset?.url))
  );
}

function formatLabel(format) {
  return String(format || 'post').replaceAll('_', ' ');
}

function creditActionDescription(action) {
  if (action?.type === 'regenerate-text') return 'This rewrites the first post text and saves a new version. It will not schedule or publish anything.';
  if (action?.type === 'full-week') return 'This generates a 7-day strategy and opens the batch builder. Nothing schedules until you confirm targets and timing.';
  if (action?.type === 'full-month') return 'This generates a 30-day plan in Content Plan. It does not create or schedule posts automatically.';
  return 'This action uses plan quota.';
}

function postPreviewTitle(content) {
  const item = content?.contentItem || {};
  const version = content?.activeVersion || {};
  const cleanTitle = String(item.title || '').replace(/^Generating:\s*/i, '').replace(/^Generating\s+\d+\/\d+:\s*/i, '').trim();
  return version.hook || item.topic || cleanTitle || 'Post preview';
}

function lifecycleIcon(status) {
  if (status === 'published') return 'checkCircle';
  if (status === 'inbox') return 'checkCircle';
  if (status === 'failed') return 'alert';
  if (status === 'planned') return 'calendar';
  if (status === 'scheduled') return 'calendar';
  if (status === 'publishing') return 'refresh';
  if (status === 'generating') return 'sparkles';
  return 'check';
}

function lifecycleFallbackMessage(status) {
  if (status === 'published') return 'This post has already been sent. Edit and schedule actions are locked.';
  if (status === 'inbox') return 'Open TikTok to review and publish the draft.';
  if (status === 'planned') return 'This scheduled post needs approval before it can publish.';
  if (status === 'scheduled') return 'This post is queued for publishing. You can still change the time.';
  if (status === 'publishing') return 'Publishing is in progress. This page updates automatically.';
  if (status === 'generating') return 'AI is preparing copy and carousel visuals.';
  if (status === 'failed') return 'This post failed before it was ready to publish. Review the post and try again.';
  return 'Review the phone preview, then schedule when it is ready.';
}

function lifecycleFailureMessage(lifecycle, previewPlatform) {
  if (lifecycle.failureKind === 'generation') {
    const reason = friendlyErrorMessage({ message: lifecycle.errorMessage }, 'AI could not create this post.');
    return `Generation failed: ${reason}`;
  }
  const platform = platformLabel(lifecycle.failedTarget?.platform || previewPlatform);
  const failedAt = lifecycle.failedAt ? ` ${formatLifecycleDate(lifecycle.failedAt)}` : '';
  const reason = friendlyErrorMessage({ message: lifecycle.errorMessage }, 'Publishing did not finish. Check the target and try again.');
  return `Publish failed on ${platform}${failedAt}: ${reason}`;
}

function previewTargetsFromContent(content, socialAccounts, platforms) {
  const summaryTargets = content?.publishSummary?.targets || [];
  const enriched = summaryTargets.map(target => {
    const account = socialAccounts.find(item => item.id === target.socialAccountId || item.id === target.social_account_id) || null;
    if (!account) return target;
    const name = displaySocialAccountName(account, { label: platformLabel(account.platform || target.platform) });
    return {
      ...target,
      name,
      secondaryText: socialAccountSecondaryText(account, name),
      socialAccount: account,
    };
  });
  if (enriched.length) return enriched;
  return platforms.map(platform => {
    const account = socialAccounts.find(item => normalizeSocialPlatform(item.platform) === normalizeSocialPlatform(platform));
    if (!account) return { platform };
    const name = displaySocialAccountName(account, { label: platformLabel(platform) });
    return {
      id: account.id,
      platform,
      name,
      secondaryText: socialAccountSecondaryText(account, name),
      socialAccount: account,
    };
  });
}

function previewTargetRows(targets, platforms, lifecycle) {
  if (targets.length) return targets;
  return platforms.map(platform => ({ platform, status: lifecycle.status, name: platformLabel(platform) }));
}

function publishTargetRowsForPlatforms(accounts, registry, platforms) {
  const targetPlatforms = normalizeTargetPlatforms(platforms);
  const platformOrder = new Map(targetPlatforms.map((platform, index) => [platform, index]));
  return (accounts || [])
    .map(account => {
      const platform = normalizeSocialPlatform(account.platform);
      if (!targetPlatforms.includes(platform)) return null;
      const catalogItem = fallbackCatalogItem(platform);
      const name = displaySocialAccountName(account, catalogItem);
      const blockedReason = publishTargetBlockedReason(account, registry || {});
      return {
        id: account.id,
        account,
        platform,
        name,
        secondaryText: socialAccountSecondaryText(account, name) || connectedCaption({ ...account, types: catalogItem.types }),
        blockedReason,
        ready: !blockedReason,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const platformDiff = (platformOrder.get(a.platform) ?? 999) - (platformOrder.get(b.platform) ?? 999);
      return platformDiff || a.name.localeCompare(b.name);
    });
}

function inlinePublishDisabledReason({ contentId, versionId, lifecycle, selectedPublishTargetRows, mode }) {
  if (!contentId || !versionId) return 'This post is still being prepared. Try again in a moment.';
  if (lifecycle.status === 'generating') return 'This post is still being generated.';
  if (lifecycle.status === 'publishing') return 'Publishing is already in progress.';
  if (lifecycle.status === 'published') return 'This post has already been published.';
  if (mode !== 'publish' && (lifecycle.status === 'scheduled' || lifecycle.status === 'planned')) return 'This post is already scheduled.';
  if (!selectedPublishTargetRows.length) return 'Choose at least one publish-ready account.';
  return null;
}

function contentVersionId(content) {
  return content?.activeVersion?.id || content?.contentVersion?.id || null;
}

function contentPlatforms(content) {
  if (Array.isArray(content?.publishSummary?.platforms) && content.publishSummary.platforms.length) {
    return normalizeTargetPlatforms(content.publishSummary.platforms);
  }
  if (Array.isArray(content?.publishPlatforms) && content.publishPlatforms.length) {
    return normalizeTargetPlatforms(content.publishPlatforms);
  }
  if (Array.isArray(content?.generationBatch?.platforms) && content.generationBatch.platforms.length) {
    return normalizeTargetPlatforms(content.generationBatch.platforms);
  }
  if (Array.isArray(content?.batch?.platforms) && content.batch.platforms.length) {
    return normalizeTargetPlatforms(content.batch.platforms);
  }
  const platforms = Object.keys(content?.activeVersion?.platformVersions || {}).filter(key => !key.startsWith('__'));
  if (platforms.length) return normalizeTargetPlatforms(platforms);
  return content?.contentItem?.format === 'image_post' ? ['instagram'] : ['linkedin'];
}

function normalizeTargetPlatforms(platforms) {
  return [...new Set((platforms || []).map(normalizeSocialPlatform).filter(Boolean))];
}

function defaultSelectedTargetIds(accounts, registry, previous, preferred = []) {
  const readyIds = new Set(accounts.filter(account => !publishTargetBlockedReason(account, registry)).map(account => account.id));
  const preferredReady = preferred.filter(id => readyIds.has(id));
  if (preferredReady.length) return preferredReady;
  const retained = previous.filter(id => readyIds.has(id));
  return [...new Set([...retained, ...Array.from(readyIds)])];
}

function publishTargetBlockedReason(account, registry) {
  const platform = normalizeSocialPlatform(account.platform);
  if (account.status !== 'connected') return 'Reconnect required before publishing';
  if (registry[platform] && !registry[platform].supportsPublish) return 'Publishing not enabled for this platform';
  if (platform === 'telegram' && account.capabilities?.destinationType && account.capabilities.destinationType !== 'channel') {
    return 'Telegram publishing requires a connected channel';
  }
  return null;
}

function mapBackendStatus(status) {
  if (status === 'connected') return 'connected';
  if (['expired', 'revoked', 'failed'].includes(status)) return 'reconnect';
  return 'not-connected';
}

function normalizeSocialPlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function socialCatalogItem(item) {
  if (item.p === 'telegram') {
    return {
      ...item,
      label: 'Telegram channel',
      name: 'No channel connected',
      note: 'Channel autoposting via Dathent bot',
      types: 'Channel posts · Broadcasts',
      permissions: ['Publish channel messages', 'Admin post access']
    };
  }
  if (item.p === 'twitter') {
    return { ...item, p: 'x', label: 'X' };
  }
  return item;
}

function buildSocialCatalog(registry) {
  const rows = Object.values(registry)
    .filter(item => item && item.enabled !== false)
    .map(item => {
      const platform = normalizeSocialPlatform(item.platform);
      return {
        p: platform,
        label: platformLabel(platform),
        name: platform === 'telegram' ? 'No channel connected' : 'Not connected',
        status: 'not-connected',
        auto: Boolean(item.mvpEnabled),
        types: capabilityTypes(item),
        recommended: Boolean(item.mvpEnabled),
        note: item.supportsPublish ? 'Publishing enabled from platform registry' : 'Connection available from platform registry',
        permissions: registryPermissions(item),
      };
    });
  if (rows.length) return rows;
  return ['instagram', 'facebook', 'threads', 'tiktok', 'x', 'telegram'].map(platform => ({
    p: platform,
    label: platformLabel(platform),
    name: platform === 'telegram' ? 'No channel connected' : 'Not connected',
    status: 'not-connected',
    auto: ['instagram', 'facebook', 'telegram'].includes(platform),
    types: platform === 'telegram' ? 'Channel posts · Broadcasts' : 'OAuth connection',
    recommended: ['instagram', 'facebook', 'telegram'].includes(platform),
    note: socialPlatformDescription(platform, null),
    permissions: platform === 'telegram' ? ['Publish channel messages', 'Admin post access'] : ['Read profile', 'Publish content'],
  }));
}

function capabilityTypes(item) {
  const pieces = [];
  if (item.supportsPublish) pieces.push('Publishing');
  if (item.supportsComments) pieces.push('Comments');
  if (item.supportsAds) pieces.push('Ads');
  if (item.supportsOauth) pieces.push('OAuth');
  return pieces.length ? pieces.join(' · ') : 'Connected account';
}

function registryPermissions(item) {
  const permissions = ['Read profile'];
  if (item.supportsPublish) permissions.unshift('Publish content');
  if (item.supportsComments) permissions.push('Manage comments');
  if (item.supportsAds) permissions.push('Ads access');
  return permissions;
}

function fallbackCatalogItem(platform) {
  return {
    p: normalizeSocialPlatform(platform),
    label: platformLabel(platform),
    name: 'Connected account',
    types: 'Connected account',
    permissions: ['Read profile']
  };
}

function describeCapabilities(capabilities, fallback, registry) {
  if (registry && !registry.supportsPublish) return ['Connect only', 'Publishing not enabled'];
  if (!capabilities || typeof capabilities !== 'object') return fallback;
  const labels = [];
  if (capabilities.authFlow === 'facebook_page') labels.push('Via Facebook Page');
  if (capabilities.authFlow === 'instagram_login') labels.push('Instagram Login');
  if (capabilities.supportsText) labels.push('Text publishing');
  if (capabilities.supportsImage) labels.push('Image publishing');
  if (capabilities.supportsCarousel) labels.push('Carousel publishing');
  return labels.length ? labels : fallback;
}

function displaySocialAccountName(account, catalogItem) {
  const value = (account.displayName || '').trim();
  if (value && !isOpaqueProviderName(value, account.externalAccountId)) {
    return value;
  }
  if (account.platform === 'instagram' && account.capabilities?.instagramUsername) {
    return `@${String(account.capabilities.instagramUsername).replace(/^@/, '')}`;
  }
  if (account.platform === 'instagram') return 'Instagram account';
  if (account.platform === 'threads') return 'Threads account';
  if (account.platform === 'facebook') return 'Facebook Page';
  if (account.platform === 'telegram') return 'Telegram channel';
  if (normalizeSocialPlatform(account.platform) === 'x') return 'X account';
  return catalogItem.label || platformLabel(account.platform);
}

function socialAccountSecondaryText(account, displayName) {
  if (account.platform === 'instagram' && account.capabilities?.authFlow === 'facebook_page') {
    const pageName = account.capabilities.pageName ? ` via ${account.capabilities.pageName}` : ' via Facebook Page';
    return `Ready for auto-publish${pageName}`;
  }
  if (account.platform === 'instagram' && displayName === 'Instagram account') {
    return 'Username unavailable from Instagram Login';
  }
  return null;
}

function isOpaqueProviderName(value, externalAccountId) {
  return /^\d{8,}$/.test(value) || (externalAccountId && value === externalAccountId);
}

function connectedCaption(account) {
  const connectedAt = formatDate(account.connectedAt);
  const prefix = account.status === 'connected' ? 'Ready' : 'Reconnect required';
  return connectedAt ? `${prefix} since ${connectedAt} · ${account.types}` : `${prefix} · ${account.types}`;
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatAppDate(date, { month: 'short', day: 'numeric' });
}

function connectLabel(platform) {
  if (platform === 'instagram') return 'Connect Instagram';
  if (platform === 'threads') return 'Connect Threads';
  if (platform === 'facebook') return 'Connect Facebook Page';
  if (platform === 'tiktok') return 'Connect TikTok';
  if (normalizeSocialPlatform(platform) === 'x') return 'Connect X';
  if (platform === 'telegram') return 'Connect channel';
  return 'Coming soon';
}

function addAnotherLabel(platform) {
  if (platform === 'telegram') return 'Add channel';
  if (platform === 'facebook') return 'Add Page';
  return 'Add another';
}

function quickAddDescription(platform) {
  const count = platform.accounts?.length || 0;
  if (count > 0) return `${count} connected. Add another ${platformLabel(platform.p)} account.`;
  return socialPlatformDescription(platform.p, platform.registry);
}

function isConnectableSocialPlatform(platform, registry = null) {
  const normalized = normalizeSocialPlatform(platform);
  if (normalized === 'x') return Boolean(registry?.supportsOauth);
  return ['instagram', 'facebook', 'threads', 'tiktok', 'telegram'].includes(normalized);
}

function socialPlatformDescription(platform, registry) {
  if (platform === 'instagram') return 'Instagram Login for Business and Creator accounts.';
  if (platform === 'facebook') return 'Facebook Page OAuth with publish-ready permissions.';
  if (platform === 'threads') return 'Threads OAuth for text-first social publishing.';
  if (platform === 'tiktok') return 'TikTok OAuth for short-form publishing setup.';
  if (normalizeSocialPlatform(platform) === 'x') return 'X OAuth for posts and account identity.';
  if (platform === 'telegram') return 'Bot setup link for channel admin publishing.';
  if (registry?.supportsPublish) return 'Publishing is registered for this platform.';
  return 'Connection setup will appear here when available.';
}

function platformLabel(platform) {
  const labels = { meta: 'Instagram', instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook Page' };
  return labels[platform] || platform;
}

function initials(value) {
  const letters = String(value || '')
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return letters || 'DT';
}

function targetDisplayName(target, platform) {
  const value = target?.name || target?.socialAccount?.displayName || target?.displayName;
  if (value && !isOpaqueProviderName(value, target?.socialAccount?.externalAccountId || target?.externalAccountId)) return value;
  return platformLabel(platform);
}

function targetMetaLine(target, platform) {
  const capabilities = target?.socialAccount?.capabilities || target?.capabilities || {};
  const count = firstNumber(capabilities.subscriberCount, capabilities.subscribers, capabilities.followerCount, capabilities.followers, capabilities.memberCount, capabilities.members);
  if (count !== null) {
    const noun = platform === 'telegram' ? 'subscribers' : 'followers';
    return `${formatCompactNumber(count)} ${noun}`;
  }
  if (platform === 'telegram' && capabilities.destinationType === 'channel') return 'Telegram channel';
  return null;
}

function targetCaptionLine(target, platform, t) {
  const meta = targetMetaLine(target, platform) || target?.secondaryText || platformLabel(platform);
  const status = targetStatusLine(target, t);
  return status ? `${status}${meta ? ` · ${meta}` : ''}` : meta;
}

function targetStatusLine(target, t) {
  const status = target?.status;
  if (status === 'published') return 'Published';
  if (status === 'publishing' || status === 'queued') return 'Publishing';
  if (status === 'pending') return 'Queued';
  if (status === 'needs_reconnect') return 'Reconnect required';
  if (status === 'needs_manual_action') return t('postPreview.inboxDraftTitle');
  if (status === 'failed') {
    const reason = publishTargetErrorMessage(target);
    return reason ? `Failed: ${friendlyErrorMessage({ message: reason }, reason)}` : 'Failed';
  }
  return null;
}

function firstNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number) && number >= 0) return number;
  }
  return null;
}

function formatCompactNumber(value) {
  try {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  } catch {
    return String(value);
  }
}

function clampProgress(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 5;
  return Math.max(5, Math.min(100, Math.round(number)));
}

function parseFutureDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date <= new Date()) return null;
  return date;
}

function contentLifecycle(content, generating, t) {
  if (generating) return { status: 'generating', label: 'Generating', message: 'Generation in progress.' };
  const summary = content?.publishSummary || null;
  const itemStatus = content?.contentItem?.status || 'ready';
  const targets = summary?.targets || [];
  const publishedAt = summary?.lastPublishedAt || null;
  const scheduledAt = summary?.lastScheduledAt || content?.publishScheduleAt || null;
  const scheduledPostId = summary?.scheduledPostId || null;
  const failedTarget = failedPublishTarget(summary);
  const inboxTarget = targets.find(target => target.status === 'needs_manual_action');
  const publishingTarget = targets.find(target => ['queued', 'publishing'].includes(target.status));
  const allTargetsPublished = targets.length > 0 && targets.every(target => target.status === 'published');
  const generationFailed = content?.contentItem?.generationStatus === 'failed' && !scheduledPostId && !failedTarget && !summary?.failedCount;
  if (generationFailed) {
    return {
      status: 'failed',
      label: 'Generation failed',
      failureKind: 'generation',
      scheduledPostId: null,
      failedTarget: null,
      failedAt: null,
      errorMessage: 'AI image generation failed before publishing started.',
      message: 'AI could not create the image for this post. Nothing was published.',
    };
  }
  if (inboxTarget && targets.every(target => ['published', 'needs_manual_action'].includes(target.status))) {
    return {
      status: 'inbox',
      label: t('postPreview.inboxDraftTitle'),
      scheduledPostId,
      message: inboxTarget.lastErrorMessage || inboxTarget.errorMessage || t('postPreview.inboxDraftBody'),
    };
  }
  if (failedTarget || summary?.failedCount > 0 || ['failed', 'partial_failure'].includes(itemStatus)) {
    return {
      status: 'failed',
      label: 'Needs retry',
      scheduledPostId,
      failedTarget,
      failedAt: failedTarget?.failedAt || failedTarget?.updatedAt || null,
      errorMessage: publishTargetErrorMessage(failedTarget),
      message: '',
    };
  }
  if (publishingTarget || summary?.postStatus === 'queued') {
    return {
      status: 'publishing',
      label: 'Publishing',
      scheduledPostId,
      scheduledAt,
      message: `Publishing to ${platformLabel(publishingTarget?.platform || targets[0]?.platform || contentPlatforms(content)[0])}. Status updates automatically.`,
    };
  }
  if (summary?.publishedCount > 0 && !allTargetsPublished) {
    return {
      status: 'publishing',
      label: 'Publishing',
      scheduledPostId,
      publishedAt,
      message: `${summary.publishedCount}/${targets.length || summary.publishedCount} targets published. Waiting for remaining targets.`,
    };
  }
  if (allTargetsPublished || (!targets.length && (summary?.postStatus === 'published' || itemStatus === 'published'))) {
    return {
      scheduledPostId,
      status: 'published',
      label: 'Published',
      publishedAt,
      message: `Published${publishedAt ? ` ${formatLifecycleDate(publishedAt)}` : ''}. Scheduling actions are locked.`,
    };
  }
  if (summary?.postStatus === 'planned') {
    return {
      status: 'planned',
      label: 'Needs approval',
      scheduledPostId,
      scheduledAt,
      message: 'Approve this scheduled post before it can publish.',
    };
  }
  if (summary?.activeCount > 0 || itemStatus === 'scheduled') {
    return {
      status: 'scheduled',
      label: 'Scheduled',
      scheduledPostId,
      scheduledAt,
      message: `Scheduled${scheduledAt ? ` for ${formatLifecycleDate(scheduledAt)}` : ''}. You can still change the publish time.`,
    };
  }
  return { status: itemStatus || 'ready', label: 'Ready', scheduledPostId: null, scheduledAt: null, message: '' };
}

function previewPrimaryAction(lifecycle, isFirstPost = false) {
  if (lifecycle.status === 'published') return { label: 'Published', icon: 'checkCircle', disabled: true };
  if (lifecycle.status === 'inbox') return { label: lifecycle.label, icon: 'checkCircle', disabled: true };
  if (lifecycle.status === 'publishing') return { label: 'Publishing', icon: 'refresh', disabled: true };
  if (lifecycle.status === 'planned') return { label: 'Open Scheduler', icon: 'calendar', disabled: false };
  if (lifecycle.status === 'failed' && lifecycle.failureKind === 'generation') return { label: 'Edit post', icon: 'edit', disabled: false };
  if (lifecycle.status === 'failed') return { label: 'Retry publish', icon: 'refresh', disabled: !lifecycle.scheduledPostId };
  if (lifecycle.status === 'scheduled') return { label: 'Change time', icon: 'calendar', disabled: false };
  if (lifecycle.status === 'generating') return { label: 'Generating', icon: 'refresh', disabled: true };
  return { label: isFirstPost ? 'Schedule this post' : 'Schedule', icon: 'calendar', disabled: false };
}

function failedPublishTarget(summary) {
  return (summary?.targets || []).find(target => ['failed', 'needs_reconnect'].includes(target.status)) || null;
}

function publishTargetErrorMessage(target) {
  if (!target) return '';
  const message = target.lastErrorMessage || target.errorMessage || '';
  const code = target.lastErrorCode || target.errorCode || '';
  if (message && code) return `${code}: ${message}`;
  return message || code || '';
}

function isInstagramBusinessPublishSetupError(lifecycle) {
  if (lifecycle?.failedTarget?.platform !== 'instagram') return false;
  const text = `${lifecycle.errorMessage || ''} ${lifecycle.failedTarget?.lastErrorCode || ''}`.toLowerCase();
  return text.includes('instagram_business_content_publish') || text.includes('business or creator') || text.includes('instagram login content publishing');
}

function markPublishRetryPending(content) {
  const summary = content?.publishSummary;
  if (!summary) return content;
  const targets = (summary.targets || []).map(target => {
    if (!['failed', 'needs_reconnect'].includes(target.status)) return target;
    return {
      ...target,
      status: 'pending',
      priority: 'retry',
      failedAt: null,
      lastErrorCode: null,
      lastErrorMessage: null,
    };
  });
  return {
    ...content,
    publishSummary: {
      ...summary,
      targets,
      failedCount: 0,
      activeCount: Math.max(1, Number(summary.activeCount || 0)),
      postStatus: 'scheduled',
    },
  };
}

function contentWithSchedule(content, scheduleResult) {
  const summary = publishSummaryFromScheduleResult(scheduleResult);
  const status = publishSummaryStatusForContent(summary);
  return {
    ...content,
    contentItem: content?.contentItem ? { ...content.contentItem, status } : content?.contentItem,
    publishSummary: summary,
    publishPlatforms: summary.platforms,
    publishTargetAccountIds: summary.targets.map(target => target.socialAccountId).filter(Boolean),
    publishMode: scheduleResult?.post?.mode === 'publish_now' ? 'now' : 'schedule',
    publishScheduleAt: summary.lastScheduledAt,
  };
}

function publishSummaryFromScheduleResult(scheduleResult) {
  const post = scheduleResult?.post || scheduleResult || {};
  const targets = Array.isArray(scheduleResult?.targets) ? scheduleResult.targets : [];
  const summary = {
    platforms: [],
    publishedPlatforms: [],
    publishedCount: 0,
    failedCount: 0,
    activeCount: 0,
    lastPublishedAt: null,
    lastFailedAt: null,
    lastScheduledAt: post.scheduledAt || null,
    scheduledPostId: post.id || null,
    postStatus: post.status || null,
    targets,
  };
  targets.forEach(target => {
    if (target.platform && !summary.platforms.includes(target.platform)) summary.platforms.push(target.platform);
    if (target.status === 'published') {
      summary.publishedCount += 1;
      if (target.platform && !summary.publishedPlatforms.includes(target.platform)) summary.publishedPlatforms.push(target.platform);
    }
    if (['failed', 'needs_reconnect'].includes(target.status)) summary.failedCount += 1;
    if (['pending', 'queued', 'publishing'].includes(target.status)) summary.activeCount += 1;
    summary.lastPublishedAt = latestDate(summary.lastPublishedAt, target.publishedAt);
    if (['failed', 'needs_reconnect'].includes(target.status)) summary.lastFailedAt = latestDate(summary.lastFailedAt, target.failedAt || target.updatedAt);
    summary.lastScheduledAt = latestDate(summary.lastScheduledAt, target.scheduledAt || post.scheduledAt);
  });
  if (summary.activeCount === 0 && ['planned', 'approved', 'queued', 'scheduled'].includes(post.status)) {
    const terminalTargetCount = summary.publishedCount + summary.failedCount + targets.filter(target => target.status === 'needs_manual_action').length;
    if (terminalTargetCount < targets.length) summary.activeCount = Math.max(1, targets.length - terminalTargetCount);
  }
  return summary;
}

function publishSummaryStatusForContent(summary) {
  if (!summary) return 'ready';
  if (summary.failedCount > 0) return 'failed';
  if (summary.targets.some(target => target.status === 'needs_manual_action')
    && summary.targets.every(target => ['published', 'needs_manual_action'].includes(target.status))) return 'inbox';
  if (summary.postStatus === 'planned') return 'planned';
  if (publishSummaryIsPublishing(summary)) return 'publishing';
  if (summary.activeCount > 0) return 'scheduled';
  if (summary.targets.length > 0) return summary.targets.every(target => target.status === 'published') ? 'published' : 'ready';
  if (summary.postStatus === 'published') return 'published';
  return 'ready';
}

function publishSummaryIsPublishing(summary) {
  const targets = Array.isArray(summary?.targets) ? summary.targets : [];
  return targets.some(target => ['queued', 'publishing'].includes(target.status))
    || targets.some(target => target.status === 'pending' && parseFutureDate(target.scheduledAt) === null);
}

function previewTargetFromSummary(content, platform, socialAccounts = []) {
  const targets = content?.publishSummary?.targets || [];
  const target = targets.find(item => normalizeSocialPlatform(item.platform) === normalizeSocialPlatform(platform)) || targets[0] || null;
  if (!target) return null;
  const account = socialAccounts.find(item => item.id === target.socialAccountId || item.id === target.social_account_id) || null;
  if (!account) return target;
  const name = displaySocialAccountName(account, { label: platformLabel(account.platform || target.platform) });
  return {
    ...target,
    name,
    secondaryText: socialAccountSecondaryText(account, name),
    socialAccount: account,
  };
}

function latestDate(current, next) {
  if (!next) return current;
  if (!current) return next;
  return Date.parse(next) > Date.parse(current) ? next : current;
}

function bestTimeDate() {
  const now = new Date();
  const candidate = new Date(now);
  candidate.setHours(10, 0, 0, 0);
  if (candidate <= now) candidate.setDate(candidate.getDate() + 1);
  return candidate;
}

function toDatetimeLocalValue(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function datetimeLocalToIso(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date <= new Date() ? null : date.toISOString();
}

function formatLifecycleDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatAppDate(date, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function readOAuthResult() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('provider') && !params.has('connected') && !params.has('error')) return null;
  return {
    provider: params.get('provider') || 'oauth',
    platform: params.get('platform') || params.get('provider') || 'oauth',
    error: params.get('error'),
    connected: params.get('connected') === '1',
    accounts: params.get('accounts') || '0',
    pages: params.get('pages'),
    instagramBusinessAccounts: params.get('instagramBusinessAccounts')
  };
}

function oauthResultTitle(result) {
  if (result.error) return `${platformLabel(result.platform)} connection needs attention`;
  if (result.connected) return `${platformLabel(result.platform)} connected`;
  return `${platformLabel(result.platform)} connection finished`;
}

function oauthResultMessage(result) {
  const messages = {
    no_instagram_business_accounts: instagramSetupMessage(result),
    no_facebook_pages: 'Meta OAuth worked, but Meta did not return any Facebook Pages for this user. Create a Facebook Page or log in with a user that has Page access, then retry the Page connection.',
    missing_code_or_state: 'The OAuth callback was missing required parameters. Start the connection again from this page.',
    invalid_or_expired_state: 'This OAuth attempt expired or no longer matches the saved state. Start the connection again.',
    instagram_token_exchange_failed: 'Instagram did not exchange the OAuth code for an access token. Check the Instagram app client ID, secret, and callback URL.',
    instagram_user_info_failed: 'Instagram connected, but profile lookup failed. Check that the Instagram Business Login permissions are enabled.',
    instagram_missing_user_id: 'Instagram did not return a usable user ID. Start the connection again after confirming the account is eligible for Instagram Business Login.',
    instagram_oauth_failed: 'Instagram did not complete the OAuth connection. Check app permissions and try again.',
    threads_token_exchange_failed: 'Threads did not exchange the OAuth code for an access token. Check the Threads app client ID, secret, and callback URL.',
    threads_user_info_failed: 'Threads connected, but profile lookup failed. Check that the Threads app permissions are enabled.',
    threads_missing_user_id: 'Threads did not return a usable user ID. Start the connection again.',
    threads_oauth_failed: 'Threads did not complete the OAuth connection. Check app permissions and try again.',
    x_token_exchange_failed: 'X did not exchange the OAuth code for an access token. Check the X app client ID, secret, callback URL, and OAuth 2.0 settings.',
    x_user_info_failed: 'X connected, but profile lookup failed. Check that the X app has users.read enabled and API access is active.',
    x_missing_user_id: 'X did not return a usable user ID. Start the connection again.',
    x_missing_pkce_verifier: 'This X OAuth attempt is missing its PKCE verifier. Start the connection again.',
    x_oauth_failed: 'X did not complete the OAuth connection. Check app permissions and try again.',
    tiktok_oauth_failed: 'TikTok did not complete the OAuth exchange. Check that the TikTok app redirect URI and requested scopes are enabled.',
    meta_oauth_failed: 'Meta did not complete the OAuth exchange. Check app permissions and try again.'
  };
  if (result.error) return messages[result.error] || `Provider returned: ${result.error}`;
  if (result.connected) return `${result.accounts} ${Number(result.accounts) === 1 ? 'account' : 'accounts'} saved to the backend. If it is not visible yet, refresh this page.`;
  return 'The provider redirected back without a connection result.';
}

function instagramSetupMessage(result) {
  const pages = result.pages === null ? null : Number(result.pages);
  const pageText = Number.isFinite(pages) ? ` Meta returned ${pages} ${pages === 1 ? 'Facebook Page' : 'Facebook Pages'}, but no linked Instagram Business account.` : '';
  return `Meta OAuth worked, but no Instagram Business account was returned.${pageText} Make sure the Instagram account is Professional/Business, linked to a Facebook Page, and this Meta user can manage that Page.`;
}
